import React, { useState, useEffect, useRef, useCallback } from 'react';
import './RecordingView.css';
import CustomDropdown from '../CustomDropdown';
import ConfirmModal from '../ConfirmModal';
import { API_BASE_URL } from '../../utils/api';

const LOCAL_RECORDER_WS = 'ws://localhost:59223';

// ---------------------------------------------------------------------------
// Dynamic values for Fill steps.
//
// The recorded value stays the default ("Static text"). A tester who wants a
// fresh value each run picks a type here; the step's value then holds a token
// like {{email|karthik.m.shetty|7edge.com}}, which tests/helpers/testdata.js
// turns into a real value at run time.
// ---------------------------------------------------------------------------
const VALUE_TYPES = [
  { value: 'static',    label: 'Static text (as recorded)' },
  { value: 'email',     label: 'Random email' },
  { value: 'phone',     label: 'Random phone number' },
  { value: 'name',      label: 'Random full name' },
  { value: 'firstName', label: 'Random first name' },
  { value: 'lastName',  label: 'Random last name' },
  { value: 'number',    label: 'Random number' },
  { value: 'digits',    label: 'Random digits' },
  { value: 'text',      label: 'Text with a unique suffix' },
  { value: 'timestamp', label: 'Timestamp' },
];

// Which option boxes each type needs, and what they default to.
const VALUE_TYPE_FIELDS = {
  email:  [{ key: 'Prefix (before the +)', def: 'test' }, { key: 'Domain (without @)', def: 'example.com' }],
  phone:  [{ key: 'Country code', def: '' }, { key: 'Starts with', def: '' }, { key: 'Total digits', def: '10' }],
  number: [{ key: 'Minimum', def: '1' }, { key: 'Maximum', def: '1000' }],
  digits: [{ key: 'How many', def: '6' }],
  text:   [{ key: 'Prefix', def: 'test' }],
};

const VALUE_TOKEN_RE = /^\{\{\s*([a-zA-Z]+)((?:\|[^}]*)*)\s*\}\}$/;

function parseValueToken(value) {
  const match = VALUE_TOKEN_RE.exec(String(value == null ? '' : value).trim());
  if (!match || !VALUE_TYPES.some(t => t.value === match[1])) {
    return { valueType: 'static', tokenArgs: [] };
  }
  return { valueType: match[1], tokenArgs: match[2] ? match[2].split('|').slice(1) : [] };
}

function buildValueToken(valueType, tokenArgs) {
  if (!valueType || valueType === 'static') return '';
  const fields = VALUE_TYPE_FIELDS[valueType] || [];
  const args = fields.map((f, i) => (tokenArgs[i] !== undefined && tokenArgs[i] !== '' ? tokenArgs[i] : f.def));
  while (args.length && args[args.length - 1] === '') args.pop();
  return `{{${valueType}${args.map(a => '|' + a).join('')}}}`;
}

// Preview only. The real values come from tests/helpers/testdata.js at run time;
// this just shows the tester the shape of what they will get.
function previewValueToken(valueType, tokenArgs) {
  const fields = VALUE_TYPE_FIELDS[valueType] || [];
  const arg = (i) => (tokenArgs[i] !== undefined && tokenArgs[i] !== '' ? tokenArgs[i] : (fields[i] ? fields[i].def : ''));
  const rand = (n) => Array.from({ length: n }, () => Math.floor(Math.random() * 10)).join('');
  const suffix = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
  switch (valueType) {
    case 'email': return `${arg(0)}+${suffix()}@${arg(1)}`;
    case 'phone': {
      // Digits only - must match tests/helpers/testdata.js exactly, or the
      // example shown here is not what the run actually types. The spaces this
      // used to add counted against the field's maxlength, so a 9-digit number
      // arrived as 7 digits.
      const starts = arg(1);
      const total = Math.max(parseInt(arg(2), 10) || 10, starts.length);
      const body = starts + rand(total - starts.length);
      return `${String(arg(0) || '').trim()}${body}`;
    }
    case 'name': return 'Meera Shetty';
    case 'firstName': return 'Meera';
    case 'lastName': return 'Shetty';
    case 'number': {
      const lo = parseInt(arg(0), 10) || 0, hi = parseInt(arg(1), 10) || 1000;
      return String(lo + Math.floor(Math.random() * Math.max(1, hi - lo + 1)));
    }
    case 'digits': return rand(parseInt(arg(0), 10) || 6);
    case 'text': return `${arg(0)} ${suffix()}`;
    case 'timestamp': return String(Date.now());
    default: return '';
  }
}

function RecordingView({ test, onBack, ws, isConnected, sendMessage, selectedApplication, onBrowserReady }) {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false); // Add pause state
  const isPausedRef = useRef(false); // Track latest paused state for ws handler
  const [isExecuting, setIsExecuting] = useState(false);
  const [testSteps, setTestSteps] = useState([]);
  const testStepsRef = useRef([]);
  const [displayedSteps, setDisplayedSteps] = useState([]); // Filtered steps without duplicates
  const [executingStepIndex, setExecutingStepIndex] = useState(-1);
  const [browserUrl, setBrowserUrl] = useState(test?.url || '');
  const [hasAutoStarted, setHasAutoStarted] = useState(false);
  // Only the setter is used - the image itself is never read back.
  const [, setViewportImage] = useState(null); // Browser viewport image (base64) - fallback
  const [cdpWsUrl, setCdpWsUrl] = useState(null); // CDP WebSocket URL for real browser
  const [streamId, setStreamId] = useState(null); // Stream ID for viewport streaming
  const [testContent, setTestContent] = useState(null);
  const [useLocalService, setUseLocalService] = useState(false); // Whether to use local Playwright service
  const [localServiceAvailable, setLocalServiceAvailable] = useState(false); // Whether local service is available
  const [localWs, setLocalWs] = useState(null); // Local service WebSocket connection
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [improvingNames, setImprovingNames] = useState(false);
  const [improveResult, setImproveResult] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  // Recording over a test that already has steps used to append silently,
  // which produced a test containing the same flow twice. Ask instead.
  const [recordChoice, setRecordChoice] = useState(null);
  const isSavingRef = useRef(false);
  useEffect(() => { isSavingRef.current = isSaving; }, [isSaving]);

  // A save that is never answered must not disable the button for good.
  //
  // "Saving" is turned off by the reply coming back - 'testSaved', or a save
  // error. If neither ever arrives (the connection died mid-save, the backend
  // restarted), the Save button stayed greyed out on "Saving..." until the
  // page was reloaded, which is one of the ways the page appeared to stop
  // responding. The heartbeat in App.js should now reconnect long before
  // this, so reaching this timeout means something unusual happened - say so
  // plainly rather than claiming the save failed, because it may well have
  // succeeded.
  useEffect(() => {
    if (!isSaving) return undefined;
    const giveUp = setTimeout(() => {
      setIsSaving(false);
      setShowMessage({
        show: true,
        message: 'No reply from the server for this save. Reload the page and check whether the test is there before saving again.',
        type: 'error'
      });
      setTimeout(() => setShowMessage({ show: false, message: '', type: 'success' }), 8000);
    }, 45000);
    return () => clearTimeout(giveUp);
  }, [isSaving]);
  const [saveSuccessMessage, setSaveSuccessMessage] = useState(null);
  const [showFillDialog, setShowFillDialog] = useState(false);
  const [pendingFill, setPendingFill] = useState(null);
  const [fillValue, setFillValue] = useState('');
  const [showTextInputDialog, setShowTextInputDialog] = useState(false);
  const [pendingTextInput, setPendingTextInput] = useState(null);
  const [textInputValue, setTextInputValue] = useState('');
  const lastFillStepIndexRef = useRef(-1); // Ref to track last fill step index for callbacks
  const [editingStep, setEditingStep] = useState(null);
  // Derived from the step's value, so an existing token reopens on the right
  // type without needing to be stored separately.
  const editingToken = parseValueToken(editingStep && editingStep.value);
  const editingValueType = editingStep && editingStep.valueType !== undefined
    ? editingStep.valueType : editingToken.valueType;
  const editingTokenArgs = editingStep && editingStep.tokenArgs !== undefined
    ? editingStep.tokenArgs : editingToken.tokenArgs;
  const [showStepEditor, setShowStepEditor] = useState(false);
  const [showMessage, setShowMessage] = useState({ show: false, message: '', type: 'success' });
  const [isEditingCode, setIsEditingCode] = useState(false);
  const [editedCode, setEditedCode] = useState('');
  const [showCodePanel, setShowCodePanel] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, message: '', onConfirm: null, type: 'danger' });
  const [testOutput, setTestOutput] = useState([]);
  const [testStatus, setTestStatus] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorDetails, setErrorDetails] = useState(null);
  const [showLeavePrompt, setShowLeavePrompt] = useState(false);
  const logsEndRef = useRef(null);
  const recentlyFilledRef = useRef(new Map()); // Track recently filled selectors with timestamps to prevent rapid duplicates
  const fillCooldownRef = useRef(null); // Track cooldown timer
  const showFillDialogRef = useRef(false); // Track dialog state via ref to avoid stale closures
  const pendingFillRef = useRef(null); // Track pending fill via ref to avoid stale closures
  const messageHandlerRef = useRef(null);
  const stepProgressTimerRef = useRef(null); // Track step progress timer
  const pendingExecuteStepsRef = useRef([]); // Queue for executeStep messages waiting for iframe
  const iframeReadyRef = useRef(false); // Track if iframe is ready for execution
  const browserReadyNotified = useRef(false);

  // Reset browser ready notification when test changes (for edit recording)
  useEffect(() => {
    browserReadyNotified.current = false;
  }, [test?.id]);

  // Notify parent once the recorder is ready - this is what clears the parent's
  // "Launching browser..." banner.
  //
  // It used to fire only when a recording actually started. That was fine while
  // opening a test ALWAYS auto-started one; now a test that already has steps
  // deliberately does not, so the banner had nothing to switch it off and sat
  // there for ever. Opening a test to look at its steps is a ready state too.
  useEffect(() => {
    if (!onBrowserReady || browserReadyNotified.current) return;

    if (cdpWsUrl || isRecording) {
      browserReadyNotified.current = true;
      onBrowserReady();
      return;
    }

    // Not launching anything: the steps are loaded and we are simply showing
    // them. Clear the banner rather than leaving it spinning.
    if (isConnected && (testSteps.length > 0 || test?.executionMode)) {
      browserReadyNotified.current = true;
      onBrowserReady();
    }
  }, [cdpWsUrl, isRecording, isConnected, testSteps.length, test?.executionMode, onBrowserReady]);
  
  // Enhanced selector information state
  const [showSelectorPanel, setShowSelectorPanel] = useState(false);
  const [selectedStepForSelector, setSelectedStepForSelector] = useState(null);

  // Helper: build Playwright test code from current steps
  const generateCodeFromSteps = useCallback((steps) => {
    const testName = test?.name || 'recorded test';
    let newCode = `import { test, expect } from '@playwright/test';\n\ntest.use({ viewport: null, launchOptions: { args: ['--start-maximized'] } }); // open maximised; the page fills the window\n\n`;
    newCode += `test('${testName}', async ({ page }) => {\n`;
    newCode += '  test.setTimeout(120000); // whole-test budget\n  page.setDefaultTimeout(30000); // per-action budget: a broken click fails here, not at 120s\n\n';
    steps.forEach(step => {
      if (step.code && !String(step.code).includes('localhost:5000')) {
        newCode += `  ${step.code.trim()}\n`;
      }
    });
    newCode += '});\n';
    return newCode;
  }, [test?.name]);

  // Keep a ref in step with the list, so handlers can read the current steps
  // without capturing a stale copy.
  useEffect(() => { testStepsRef.current = testSteps; }, [testSteps]);

  // Which locator an assert should target.
  //
  // If the tester has not changed the Selector field, reuse the locator exactly
  // as Playwright wrote it for the click. That guarantees the assert finds the
  // same element the click just found. Rebuilding it as page.locator('...')
  // silently changed the matching rules and broke asserts that should have passed.
  //
  // If they did change it, respect what they typed - either a full locator
  // expression starting with "page.", or a plain selector.
  // Used by EVERY action, not just asserts: re-expressing a recorded locator
  // chain as page.locator('css') can target a different element entirely.
  const assertTarget = (step) => {
    const typed = (step.selector || '').trim();
    const unchanged = !step.derivedSelector || typed === (step.derivedSelector || '').trim();

    if (step.locatorExpr && unchanged) return step.locatorExpr;
    if (typed.startsWith('page.')) return typed;
    return `page.locator('${typed}')`;
  };

  // Map a step row returned by the API into the shape used by testSteps state.
  const mapServerStep = useCallback((s, idx) => ({
    id: s.step_number || idx + 1,
    action: s.action,
    selector: s.selector,
    value: s.value,
    code: s.code,
    actual: s.actual,
    expected: s.expected,
    dbId: s.id || s.step_id,
    duration: 0,
    status: 'completed'
  }), []);

  // Saving a test replaces every test_steps row, so the DB ids held in state go
  // stale and the next edit/delete targets a row that no longer exists
  // ("Step not found"). Re-sync from the server after every save.
  const refreshStepsFromServer = useCallback(async (testId, stepsFromResponse) => {
    if (Array.isArray(stepsFromResponse)) {
      setTestSteps(stepsFromResponse.map(mapServerStep));
      return;
    }
    if (!testId) return;
    try {
      const testResponse = await fetch(`${API_BASE_URL}/api/test-cases/${testId}`);
      const testData = await testResponse.json();
      if (testData.success && Array.isArray(testData.testCase?.steps)) {
        setTestSteps(testData.testCase.steps.map(mapServerStep));
      }
    } catch (fetchErr) {
      console.error('Error refreshing steps after save:', fetchErr);
    }
  }, [mapServerStep]);

  // Insert a default wait(1000ms) step above or below the selected step and persist
  const insertWaitStep = useCallback(async (index, position = 'above') => {
    const waitMs = 1000;
    let snapshot = [];
    setTestSteps(prev => {
      const newSteps = [...prev];
      const insertIndex = position === 'above' ? index : index + 1;
      newSteps.splice(insertIndex, 0, {
        id: Date.now(), // temporary id, will be normalized below
        action: `Wait for ${waitMs} ms`,
        englishDescription: `Wait for ${waitMs} ms`,
        actionType: 'wait',
        value: waitMs.toString(),
        code: `await page.waitForTimeout(${waitMs});`,
        selector: null,
        status: 'completed',
        duration: 0
      });
      snapshot = newSteps.map((s, idx) => ({ ...s, id: idx + 1 }));
      return snapshot;
    });

    if (!snapshot || snapshot.length === 0) return;

    const newCode = generateCodeFromSteps(snapshot);
    setTestContent(newCode);
    if (showCodePanel && !isEditingCode) {
      setEditedCode(newCode);
    }

    const testId = test?.dbId || test?.id || test?.test_case_id;
    if (testId) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/test-cases/${testId}/code`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ content: newCode })
        });
        const data = await response.json();
        if (!data.success) {
          console.warn('Failed to persist inserted wait step:', data.error);
        } else {
          await refreshStepsFromServer(testId, data.steps);
        }
      } catch (err) {
        console.warn('Error persisting inserted wait step:', err);
      }
    }

    setShowMessage({ show: true, message: 'Wait step added', type: 'success' });
    setTimeout(() => setShowMessage({ show: false, message: '', type: 'success' }), 2000);
  }, [generateCodeFromSteps, showCodePanel, isEditingCode, test, refreshStepsFromServer]);

  // Handle execution mode - start test execution when test has executionMode flag
  useEffect(() => {
    const testId = test?.dbId || test?.id || test?.test_case_id;
    if (test?.executionMode && testId && ws && ws.readyState === WebSocket.OPEN) {
      setIsExecuting(true);
      setExecutingStepIndex(-1);
      setTestStatus(null);
      setTestOutput([]);
      
      // Load test steps first
      const loadAndRun = async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/api/test-cases/${testId}`);
          const data = await response.json();
          if (data.success && data.testCase && data.testCase.steps) {
            const steps = data.testCase.steps.map((step, index) => ({
              id: step.step_number || index + 1,
              action: step.action,
              selector: step.selector,
              value: step.value,
              code: step.code || step.action,
              actual: step.actual,
              expected: step.expected,
              duration: 0,
              status: 'pending'
            }));
            setTestSteps(steps);
            
            // Get test file path
            let testFile = test.filename;
            if (!testFile && test.name) {
              const sanitizedName = test.name.replace(/[^a-zA-Z0-9]/g, '_').replace(/\s+/g, '_');
              testFile = `tests/${sanitizedName}.spec.ts`;
            } else if (!testFile) {
              testFile = `tests/test.spec.ts`;
            }
            
            // Ensure filename is relative and starts with 'tests/'
            if (testFile) {
              testFile = testFile.replace(/^.*\/tests\//, 'tests/').replace(/^\.\//, '');
              if (!testFile.startsWith('tests/')) {
                testFile = `tests/${testFile}`;
              }
            }
            
            // Set browser URL from test if available - use proxy URL for cross-origin with execution mode
            // BUT: Never use viewport-viewer.html URLs during execution
            if (data.testCase.url && !data.testCase.url.includes('viewport-viewer.html')) {
              const testUrl = data.testCase.url;
              // If it's a cross-origin URL, use proxy with execution mode
              if (testUrl.startsWith('http://') || testUrl.startsWith('https://')) {
                const proxyUrl = `${API_BASE_URL}/proxy?target=${encodeURIComponent(testUrl)}&execution=true`;
                setBrowserUrl(proxyUrl);
                console.log('[FRONTEND] Set browser URL from test case:', proxyUrl);
              } else {
                setBrowserUrl(testUrl);
                console.log('[FRONTEND] Set browser URL from test case:', testUrl);
              }
            } else if (steps.length > 0) {
              // If no URL in test case, try to get it from the first Navigate step
              const firstNavigateStep = steps.find(step => 
                step.action && step.action.includes('Navigate') && (step.value || step.code)
              );
              if (firstNavigateStep) {
                let stepUrl = firstNavigateStep.value;
                if (!stepUrl && firstNavigateStep.code) {
                  // Extract URL from code
                  const urlMatch = firstNavigateStep.code.match(/goto\(['"]([^'"]+)['"]\)|Navigate to (https?:\/\/[^\s]+)/i);
                  if (urlMatch) {
                    stepUrl = urlMatch[1] || urlMatch[2];
                  }
                }
                if (stepUrl && (stepUrl.startsWith('http://') || stepUrl.startsWith('https://'))) {
                  const proxyUrl = `${API_BASE_URL}/proxy?target=${encodeURIComponent(stepUrl)}&execution=true`;
                  setBrowserUrl(proxyUrl);
                  console.log('[FRONTEND] Set browser URL from first Navigate step:', proxyUrl);
                }
              }
            }
            
            // Clean up any existing message handler
            if (messageHandlerRef.current && ws) {
              ws.removeEventListener('message', messageHandlerRef.current);
              messageHandlerRef.current = null;
            }
            
            // Track current executing step index using a ref to avoid stale closures
            const currentStepIndexRef = { current: -1 };
            
            
            // Listen for test execution messages from WebSocket (backend executes using Playwright)
            const messageHandler = (event) => {
              try {
                // Handle WebSocket messages
                if (!event.data || typeof event.data === 'string') {
                  const data = JSON.parse(event.data || '{}');
                  console.log('[FRONTEND] WebSocket message received:', data.type, data);
                  
                  // In iframe execution mode, ignore stdout/stderr (no Playwright process)
                  if (test?.executionMode && (data.type === 'stdout' || data.type === 'stderr')) {
                    // Ignore Playwright output in iframe execution mode
                    console.log('[FRONTEND] Ignoring Playwright output in execution mode:', data.type);
                    return;
                  }
                } else {
                  return; // Not a WebSocket message
                }
                
                // Handle test execution output (only for non-iframe execution)
                if (data.type === 'stdout' || data.type === 'stderr') {
                  setTestOutput(prev => [...prev, { type: data.type, message: data.data }]);
                  
                  const output = data.data || '';
                  
                  // Clear auto-advance timer and restart it
                  if (stepProgressTimerRef.current) {
                    clearInterval(stepProgressTimerRef.current);
                    stepProgressTimerRef.current = null;
                  }
                  
                  // Pattern 1: Test execution start
                  if (output.includes('Running') && output.includes('test')) {
                    setTestStatus('running');
                    if (currentStepIndexRef.current < 0) {
                      currentStepIndexRef.current = 0;
                      setExecutingStepIndex(0);
                      setTestSteps(prev => {
                        if (prev.length > 0) {
                          return prev.map((step, idx) => 
                            idx === 0 ? { ...step, status: 'running' } : step
                          );
                        }
                        return prev;
                      });
                    }
                    // Progress is now tracked via stepExecuted messages from backend
                  }
                  
                  // Pattern 2: Look for step-related keywords in output
                  const stepKeywords = [
                    { keyword: 'goto', action: 'Navigate' },
                    { keyword: 'fill', action: 'Fill' },
                    { keyword: 'click', action: 'Click' },
                    { keyword: 'getByText', action: 'Click' },
                    { keyword: 'locator', action: 'Click' },
                    { keyword: 'press', action: 'Press' },
                    { keyword: 'selectOption', action: 'Select' },
                    { keyword: 'check', action: 'Check' },
                    { keyword: 'uncheck', action: 'Uncheck' },
                    { keyword: 'hover', action: 'Hover' },
                    { keyword: 'waitFor', action: 'Wait' },
                    { keyword: 'expect', action: 'Assert' }
                  ];
                  
                  // Check if output contains any step keyword
                  for (const { keyword, action } of stepKeywords) {
                    if (output.toLowerCase().includes(keyword.toLowerCase())) {
                      // Find matching step in our list
                      setTestSteps(prev => {
                        const newSteps = [...prev];
                        const currentIdx = currentStepIndexRef.current;
                        
                        // Mark current step as completed
                        if (currentIdx >= 0 && currentIdx < newSteps.length) {
                          if (newSteps[currentIdx].status === 'running') {
                            newSteps[currentIdx] = { ...newSteps[currentIdx], status: 'completed' };
                          }
                        }
                        
                        // Find next step matching this action type
                        const nextStepIndex = newSteps.findIndex((step, idx) => 
                          idx > currentIdx && 
                          (step.action.toLowerCase().includes(action.toLowerCase()) ||
                           step.code?.toLowerCase().includes(keyword.toLowerCase()))
                        );
                        
                        if (nextStepIndex >= 0) {
                          currentStepIndexRef.current = nextStepIndex;
                          setExecutingStepIndex(nextStepIndex);
                          newSteps[nextStepIndex] = { ...newSteps[nextStepIndex], status: 'running' };
                        } else if (currentIdx < newSteps.length - 1) {
                          // Move to next step if no match
                          const nextIdx = currentIdx + 1;
                          currentStepIndexRef.current = nextIdx;
                          setExecutingStepIndex(nextIdx);
                          newSteps[nextIdx] = { ...newSteps[nextIdx], status: 'running' };
                        }
                        
                        return newSteps;
                      });
                      break;
                    }
                  }
                  
                  // Pattern 3: Test passed
                  if (output.includes('✓') || (output.includes('passed') && !output.includes('failed'))) {
                    setTestSteps(prev => prev.map((step, idx) => {
                      if (step.status === 'running') {
                        return { ...step, status: 'completed' };
                      } else if (step.status === 'pending' && idx <= currentStepIndexRef.current) {
                        return { ...step, status: 'completed' };
                      }
                      return step;
                    }));
                  }
                  
                  // Pattern 4: Test failure
                  if (output.includes('✘') || output.includes('failed') || output.includes('Error:') || output.includes('Timeout')) {
                    setTestSteps(prev => prev.map((step, idx) => {
                      if (step.status === 'running') {
                        return { ...step, status: 'failed' };
                      } else if (idx === currentStepIndexRef.current) {
                        return { ...step, status: 'failed' };
                      }
                      return step;
                    }));
                  }
                } else if (data.type === 'testExecutionStarted') {
                  // Backend started executing test - show iframe with target URL
                  setTestOutput(prev => [...prev, { type: 'info', message: data.message }]);
                  setTestStatus('running');
                  setIsExecuting(true);
                  
                  // Clear any previous browserUrl (especially viewport-viewer.html from recording)
                  setBrowserUrl('');
                  // Reset iframe ready state
                  iframeReadyRef.current = false;
                  pendingExecuteStepsRef.current = [];
                  
                  // Set browser URL for iframe execution
                  if (data.targetUrl && !data.targetUrl.includes('viewport-viewer.html')) {
                    let proxyUrl = data.targetUrl;
                    // Use proxy URL for cross-origin with execution mode
                    if (!proxyUrl.startsWith(`${API_BASE_URL}/proxy`) && !proxyUrl.startsWith('http://localhost:3000')) {
                      const encodedUrl = encodeURIComponent(proxyUrl);
                      proxyUrl = `${API_BASE_URL}/proxy?target=${encodedUrl}&execution=true`;
                    }
                    // Set immediately (no delay needed)
                    setBrowserUrl(proxyUrl);
                    console.log('[FRONTEND] Setting browser URL for execution:', proxyUrl);
                    console.log('[FRONTEND] Iframe will load and execution script will be injected');
                  } else {
                    if (data.targetUrl && data.targetUrl.includes('viewport-viewer.html')) {
                      console.error('[FRONTEND] ERROR: targetUrl contains viewport-viewer.html, ignoring it');
                    } else {
                      console.warn('[FRONTEND] testExecutionStarted received but no valid targetUrl provided');
                    }
                  }
                  
                  // Update steps from backend if provided
                  if (data.steps && Array.isArray(data.steps) && data.steps.length > 0) {
                    const formattedSteps = data.steps.map((step, index) => ({
                      id: step.step_number || index + 1,
                      action: step.action || step.code || '',
                      selector: step.selector,
                      value: step.value,
                      code: step.code || step.action,
                      actual: step.actual,
                      expected: step.expected,
                      duration: 0,
                      status: index === 0 ? 'running' : 'pending'
                    }));
                    setTestSteps(formattedSteps);
                    setExecutingStepIndex(0);
                    console.log('[FRONTEND] Initialized', formattedSteps.length, 'steps from testExecutionStarted');
                  } else {
                    // Mark first step as running if steps already exist
                    setTestSteps(prev => {
                      const newSteps = [...prev];
                      if (newSteps.length > 0) {
                        newSteps[0] = { ...newSteps[0], status: 'running' };
                        setExecutingStepIndex(0);
                      }
                      return newSteps;
                    });
                  }
                } else if (data.type === 'executeStep') {
                  // Backend wants to execute a step in the iframe
                  const { stepIndex, step } = data;
                  console.log('[FRONTEND] ========== RECEIVED EXECUTE STEP COMMAND ==========');
                  console.log('[FRONTEND] Step Index:', stepIndex);
                  console.log('[FRONTEND] Step Action:', step.action);
                  console.log('[FRONTEND] Step Selector:', step.selector);
                  console.log('[FRONTEND] Step Value:', step.value);
                  
                  // Function to find and send to iframe
                  const sendToIframe = (retryCount = 0) => {
                    // Try multiple selectors to find the iframe
                    let iframe = document.querySelector('.browser-iframe') || 
                                 document.querySelector('iframe[src*="proxy"]') ||
                                 document.querySelector('iframe.execution-iframe') ||
                                 document.querySelector('iframe');
                    
                    // Check if iframe is ready (for execution mode)
                    const isIframeReady = iframe && (
                      iframe.dataset.ready === 'true' || 
                      iframeReadyRef.current ||
                      (iframe.contentWindow && iframe.contentDocument)
                    );
                    
                    if (iframe && iframe.contentWindow) {
                      // If iframe is not ready yet and we're in execution mode, queue the message
                      if (test?.executionMode && !isIframeReady && retryCount < 10) {
                        console.log(`[FRONTEND] Iframe not ready yet, queuing executeStep (retry ${retryCount + 1}/10)...`);
                        // Add to pending queue if not already there
                        const alreadyQueued = pendingExecuteStepsRef.current.some(
                          p => p.stepIndex === stepIndex
                        );
                        if (!alreadyQueued) {
                          pendingExecuteStepsRef.current.push({ step, stepIndex });
                          console.log('[FRONTEND] Queued executeStep, stepIndex:', stepIndex);
                        }
                        // Retry after a delay
                        setTimeout(() => sendToIframe(retryCount + 1), 500);
                        return;
                      }
                      
                      try {
                        // Check if iframe is accessible (not cross-origin blocked)
                        try {
                          // Try to access iframe's document to verify it's ready
                          const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
                          if (!iframeDoc && retryCount < 5 && !test?.executionMode) {
                            // Iframe might still be loading, retry (only for non-execution mode)
                            console.log(`[FRONTEND] Iframe document not ready, retrying (${retryCount + 1}/5)...`);
                            setTimeout(() => sendToIframe(retryCount + 1), 300);
                            return;
                          }
                        } catch (e) {
                          // Cross-origin - can't access document, but can still send postMessage
                          console.log('[FRONTEND] Cannot access iframe document (cross-origin), but can send postMessage');
                        }
                        
                        // Send the message
                        iframe.contentWindow.postMessage({
                          type: 'executeStep',
                          step: step,
                          stepIndex: stepIndex
                        }, '*');
                        console.log('[FRONTEND] ✓ Sent executeStep to iframe, stepIndex:', stepIndex, 'action:', step.action);
                        
                        // Remove from pending queue if it was there
                        pendingExecuteStepsRef.current = pendingExecuteStepsRef.current.filter(
                          p => p.stepIndex !== stepIndex
                        );
                      } catch (e) {
                        console.error('[FRONTEND] Error sending command to iframe:', e);
                        if (retryCount < 3) {
                          console.log(`[FRONTEND] Retrying send (${retryCount + 1}/3)...`);
                          setTimeout(() => sendToIframe(retryCount + 1), 500);
                        } else {
                          // Send failure back to backend after retries exhausted
                          if (sendMessage) {
                            sendMessage('stepCompleted', { stepIndex, success: false, error: e.message });
                          } else if (ws && ws.readyState === WebSocket.OPEN) {
                            ws.send(JSON.stringify({ action: 'stepCompleted', payload: { stepIndex, success: false, error: e.message } }));
                          }
                        }
                      }
                    } else {
                      if (retryCount < 10) {
                        console.log(`[FRONTEND] Iframe not found, retrying (${retryCount + 1}/10)...`);
                        // Queue message if in execution mode
                        if (test?.executionMode) {
                          const alreadyQueued = pendingExecuteStepsRef.current.some(
                            p => p.stepIndex === stepIndex
                          );
                          if (!alreadyQueued) {
                            pendingExecuteStepsRef.current.push({ step, stepIndex });
                            console.log('[FRONTEND] Queued executeStep (iframe not found), stepIndex:', stepIndex);
                          }
                        }
                        setTimeout(() => sendToIframe(retryCount + 1), 500);
                      } else {
                        console.error('[FRONTEND] Iframe not found or not ready after retries');
                        // Send failure back to backend
                        if (sendMessage) {
                          sendMessage('stepCompleted', { stepIndex, success: false, error: 'Iframe not ready after retries' });
                        } else if (ws && ws.readyState === WebSocket.OPEN) {
                          ws.send(JSON.stringify({ action: 'stepCompleted', payload: { stepIndex, success: false, error: 'Iframe not ready after retries' } }));
                        }
                      }
                    }
                  };
                  
                  // If it's a Navigate step, wait longer for iframe to be ready
                  // Otherwise, wait a bit for page to be ready
                  const delay = step.action && step.action.includes('Navigate') ? 1000 : 500;
                  setTimeout(() => {
                    sendToIframe(0);
                  }, delay);
                } else if (data.type === 'stepExecuted') {
                  // Backend sent step execution result
                  const { stepIndex, result, step } = data;
                  console.log('[FRONTEND] Received stepExecuted from backend:', stepIndex, result);
                  
                  // Determine status from result message or success flag
                  const statusMessage = result?.message || '';
                  const isRunning = statusMessage === 'running' || statusMessage.includes('Executing');
                  const isCompleted = result?.success === true || statusMessage === 'completed' || statusMessage.includes('Completed');
                  const isFailed = result?.success === false || statusMessage === 'failed' || statusMessage.includes('failed');
                  
                  // Update step status based on the result
                  setTestSteps(prev => {
                    const newSteps = [...prev];
                    if (stepIndex >= 0 && stepIndex < newSteps.length) {
                      if (isRunning) {
                        // Mark step as running
                        newSteps[stepIndex] = { ...newSteps[stepIndex], status: 'running' };
                        setExecutingStepIndex(stepIndex);
                        // Add output message
                        setTestOutput(prevOutput => [...prevOutput, { 
                          type: 'info', 
                          message: result?.message || `Step ${stepIndex + 1}: ${step?.action || 'Executing...'}` 
                        }]);
                      } else if (isCompleted) {
                        // Mark step as completed
                        newSteps[stepIndex] = { ...newSteps[stepIndex], status: 'completed' };
                        // Add success message
                        setTestOutput(prevOutput => [...prevOutput, { 
                          type: 'success', 
                          message: result?.message || `Step ${stepIndex + 1} completed: ${step?.action || ''}` 
                        }]);
                        
                        // Move to next step
                        const nextIndex = stepIndex + 1;
                        if (nextIndex < newSteps.length) {
                          setExecutingStepIndex(nextIndex);
                          // Mark next step as running (will be confirmed by next stepExecuted message)
                          newSteps[nextIndex] = { ...newSteps[nextIndex], status: 'running' };
                        } else {
                          // All steps completed
                          setTestStatus('success');
                          setIsExecuting(false);
                          setExecutingStepIndex(-1);
                        }
                      } else if (isFailed) {
                        // Mark step as failed
                        newSteps[stepIndex] = { ...newSteps[stepIndex], status: 'failed' };
                        setTestStatus('failed');
                        setIsExecuting(false);
                        setExecutingStepIndex(-1);
                        // Add error message
                        setTestOutput(prevOutput => [...prevOutput, { 
                          type: 'error', 
                          message: result?.message || `Step ${stepIndex + 1} failed: ${step?.action || ''}` 
                        }]);
                      }
                    }
                    return newSteps;
                  });
                } else if (data.type === 'testExecutionCompleted') {
                  // Backend completed test execution
                  setTestStatus(data.success ? 'success' : 'failed');
                  setIsExecuting(false);
                  setTestOutput(prev => [...prev, { 
                    type: data.success ? 'success' : 'error', 
                    message: data.message 
                  }]);
                  
                  // Mark all remaining steps
                  setTestSteps(prev => prev.map((step, idx) => {
                    if (step.status === 'running') {
                      return { ...step, status: data.success ? 'completed' : 'failed' };
                    } else if (step.status === 'pending' && !data.success) {
                      return { ...step, status: 'failed' };
                    }
                    return step;
                  }));
                  
                  setExecutingStepIndex(-1);
                } else if (data.type === 'testStarted') {
                  setTestOutput(prev => [...prev, { type: 'info', message: data.message }]);
                  setTestStatus('running');
                  if (currentStepIndexRef.current < 0) {
                    currentStepIndexRef.current = 0;
                    setExecutingStepIndex(0);
                    setTestSteps(prev => {
                      if (prev.length > 0) {
                        return prev.map((step, idx) => 
                          idx === 0 ? { ...step, status: 'running' } : step
                        );
                      }
                      return prev;
                    });
                  }
                  // Progress is now tracked via stepExecuted messages from backend
                } else if (data.type === 'testFinished') {
                  // Clear auto-advance timer
                  if (stepProgressTimerRef.current) {
                    clearInterval(stepProgressTimerRef.current);
                    stepProgressTimerRef.current = null;
                  }
                  
                  setTestStatus(data.success ? 'success' : 'failed');
                  setTestOutput(prev => [...prev, { 
                    type: data.success ? 'success' : 'error', 
                    message: `Test ${data.success ? 'passed' : 'failed'} with exit code ${data.exitCode}` 
                  }]);
                  
                  // Mark all remaining steps
                  setTestSteps(prev => prev.map((step, idx) => {
                    if (step.status === 'running') {
                      return { ...step, status: data.success ? 'completed' : 'failed' };
                    } else if (step.status === 'pending') {
                      if (idx <= currentStepIndexRef.current) {
                        return { ...step, status: data.success ? 'completed' : 'failed' };
                      }
                    }
                    return step;
                  }));
                  
                  setIsExecuting(false);
                  
                  // Remove listener after test finishes
                  if (ws && messageHandlerRef.current) {
                    ws.removeEventListener('message', messageHandlerRef.current);
                    messageHandlerRef.current = null;
                  }
                } else if (data.type === 'error' && data.message && (data.message.includes('test') || data.message.includes('Failed to run'))) {
                  // Clear auto-advance timer
                  if (stepProgressTimerRef.current) {
                    clearInterval(stepProgressTimerRef.current);
                    stepProgressTimerRef.current = null;
                  }
                  
                  setTestStatus('error');
                  setTestOutput(prev => [...prev, { type: 'error', message: data.message }]);
                  setIsExecuting(false);
                  
                  // Mark all steps as failed
                  setTestSteps(prev => prev.map(step => ({ ...step, status: 'failed' })));
                  
                  if (ws && messageHandlerRef.current) {
                    ws.removeEventListener('message', messageHandlerRef.current);
                    messageHandlerRef.current = null;
                  }
                }
              } catch (error) {
                console.error('Error parsing test execution message:', error);
              }
            };
            
            messageHandlerRef.current = messageHandler;
            if (ws) {
              ws.addEventListener('message', messageHandler);
            }
            
            // Send runTest message via WebSocket - backend will execute using Playwright
            const testId = test?.dbId || test?.id || test?.test_case_id || data.testCase?.id;
            console.log('[FRONTEND] Sending runTest message, testId:', testId, 'testFile:', testFile, 'test object:', { dbId: test?.dbId, id: test?.id, test_case_id: test?.test_case_id, testCaseId: data.testCase?.id });
            
            if (sendMessage) {
              sendMessage('runTest', { testFile, testCaseId: testId });
            } else if (ws && ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ action: 'runTest', payload: { testFile, testCaseId: testId } }));
            } else {
              console.error('[FRONTEND] Cannot send runTest - WebSocket not ready, state:', ws ? ws.readyState : 'null');
            }
            
            // Mark first step as running
            if (steps.length > 0) {
              setExecutingStepIndex(0);
              setTestStatus('running');
              setIsExecuting(true);
              setTestSteps(prev => {
                const newSteps = [...prev];
                if (newSteps.length > 0) {
                  newSteps[0] = { ...newSteps[0], status: 'running' };
                }
                return newSteps;
              });
            }
          }
        } catch (error) {
          console.error('Error loading test for execution:', error);
          setShowMessage({ show: true, message: `Error loading test: ${error.message}`, type: 'error' });
          setTimeout(() => setShowMessage({ show: false, message: '', type: 'success' }), 5000);
        }
      };
      
      loadAndRun();
      
      // Cleanup on unmount
      return () => {
        if (ws && messageHandlerRef.current) {
          ws.removeEventListener('message', messageHandlerRef.current);
          messageHandlerRef.current = null;
        }
        // Clear any timers
        if (stepProgressTimerRef.current) {
          clearInterval(stepProgressTimerRef.current);
          stepProgressTimerRef.current = null;
        }
      };
    }
  }, [test?.executionMode, test?.dbId, test?.id, test?.test_case_id, ws, sendMessage, test?.filename, test?.name]);

  // Load test steps from database if test has dbId
  useEffect(() => {
    const testId = test?.dbId || test?.id || test?.test_case_id;
    if (testId && !test?.executionMode) {
      const loadTestSteps = async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/api/test-cases/${testId}`);
          const data = await response.json();
          if (data.success && data.testCase) {
            // Set test content
            if (data.testCase.content) {
              setTestContent(data.testCase.content);
            }
            // Load steps
            if (data.testCase.steps && data.testCase.steps.length > 0) {
              const steps = data.testCase.steps.map((step, index) => {
                // Build action text from code if available, otherwise use action + selector + value
                let actionText = step.action;
                
                // Apply the same cleaning logic as the backend for consistency
                const cleanActionDescription = (originalAction, selector, code) => {
                  if (!originalAction) return '';
                  
                  console.log('[FRONTEND-CLEAN] 🔍 Cleaning action:', originalAction);
                  console.log('[FRONTEND-CLEAN] 🔍 Selector:', selector);
                  console.log('[FRONTEND-CLEAN] 🔍 Code:', code);
                  
                  // If it's already a clean description, return as-is
                  if (!originalAction.includes('testid data testid') && 
                      !originalAction.includes('internal:') && 
                      !originalAction.includes('testid=') &&
                      !originalAction.match(/Click\s+[a-f0-9-]{36}/) && // UUID patterns
                      !originalAction.match(/Click\s+\w+\s+\w+\s+\w+\s+\w+\s+s$/)) { // "Click testid data testid X Y Z s" pattern
                    console.log('[FRONTEND-CLEAN] ✅ Already clean, returning as-is');
                    return originalAction;
                  }
                  
                  // First try to extract from code if available
                  if (code) {
                    // Extract from getByTestId in code
                    const testIdMatch = code.match(/getByTestId\(['"]([^'"]+)['"]\)/);
                    if (testIdMatch) {
                      const cleanName = testIdMatch[1].replace(/_/g, ' ').replace(/-/g, ' ');
                      if (originalAction.toLowerCase().includes('click')) {
                        const result = `Click ${cleanName}`;
                        console.log('[FRONTEND-CLEAN] ✅ Extracted from getByTestId code:', result);
                        return result;
                      } else if (originalAction.toLowerCase().includes('fill')) {
                        const valueMatch = originalAction.match(/["']([^"']+)["']/);
                        const value = valueMatch ? valueMatch[1] : '';
                        const lowerCleanName = cleanName.toLowerCase();
                        if (lowerCleanName.includes('email')) {
                          return `Fill "${value}" into the email field`;
                        } else if (lowerCleanName.includes('password')) {
                          return `Fill "${value}" into the password field`;
                        } else {
                          return `Fill "${value}" into the ${cleanName} field`;
                        }
                      }
                    }
                    
                    // Extract from getByText in code
                    const textMatch = code.match(/getByText\(['"]([^'"]+)['"]\)/);
                    if (textMatch) {
                      const result = `Click "${textMatch[1]}"`;
                      console.log('[FRONTEND-CLEAN] ✅ Extracted from getByText code:', result);
                      return result;
                    }
                  }
                  
                  // Extract clean name from selector using the same logic as backend
                  const extractCleanName = (sel) => {
                    if (!sel) return '';
                    
                    console.log('[FRONTEND-CLEAN] 🔍 Processing selector:', sel);
                    
                    // Remove "internal:" prefix
                    let cleaned = sel.replace(/^internal:/, '');
                    
                    // Handle "testid data testid X Y Z s" format (from screenshot)
                    const testidDataTestidMatch = cleaned.match(/^testid\s+data\s+testid\s+(.+?)\s+s$/i);
                    if (testidDataTestidMatch) {
                      const result = testidDataTestidMatch[1].replace(/_/g, ' ').replace(/-/g, ' ').trim();
                      console.log('[FRONTEND-CLEAN] ✅ Matched testid data testid format:', result);
                      return result;
                    }
                    
                    // Handle "div >> internal:has text /^Text$/" format
                    const hasTextMatch = cleaned.match(/has\s+text\s+\/\^([^$/]+)\$?\//);
                    if (hasTextMatch) {
                      const result = hasTextMatch[1];
                      console.log('[FRONTEND-CLEAN] ✅ Matched has text format:', result);
                      return result;
                    }
                    
                    // Extract data-testid value (standard format)
                    const testIdMatch = cleaned.match(/testid=\[data-testid=["']([^"']+)["']\]/) || 
                                       cleaned.match(/\[data-testid=["']([^"']+)["']\]/);
                    if (testIdMatch) {
                      const result = testIdMatch[1].replace(/_/g, ' ').replace(/-/g, ' ');
                      console.log('[FRONTEND-CLEAN] ✅ Matched standard testid format:', result);
                      return result;
                    }
                    
                    // Extract role with name (very user-friendly)
                    const roleMatch = cleaned.match(/role=([^[]+)\[name=["']([^"']+)["']\]/);
                    if (roleMatch) {
                      const result = roleMatch[2].replace(/_/g, ' ').replace(/-/g, ' ');
                      console.log('[FRONTEND-CLEAN] ✅ Matched role format:', result);
                      return result;
                    }
                    
                    // Extract name attribute (user-friendly)
                    const nameMatch = cleaned.match(/\[name=["']([^"']+)["']\]/);
                    if (nameMatch) {
                      const result = nameMatch[1].replace(/_/g, ' ').replace(/-/g, ' ');
                      console.log('[FRONTEND-CLEAN] ✅ Matched name format:', result);
                      return result;
                    }
                    
                    // Extract id (clean and simple)
                    const idMatch = cleaned.match(/#([^.\s[]+)/);
                    if (idMatch) {
                      const result = idMatch[1].replace(/_/g, ' ').replace(/-/g, ' ');
                      console.log('[FRONTEND-CLEAN] ✅ Matched id format:', result);
                      return result;
                    }
                    
                    // Extract class (less preferred but still useful)
                    const classMatch = cleaned.match(/\.([^#\s[]+)/);
                    if (classMatch) {
                      const result = classMatch[1].replace(/_/g, ' ').replace(/-/g, ' ');
                      console.log('[FRONTEND-CLEAN] ✅ Matched class format:', result);
                      return result;
                    }
                    
                    // Extract text content from getByText selectors
                    const textMatch = cleaned.match(/getByText\(['"]([^"']+)["']\)/);
                    if (textMatch) {
                      const result = textMatch[1];
                      console.log('[FRONTEND-CLEAN] ✅ Matched getByText format:', result);
                      return result;
                    }
                    
                    // Extract placeholder text
                    const placeholderMatch = cleaned.match(/\[placeholder=["']([^"']+)["']\]/);
                    if (placeholderMatch) {
                      const result = placeholderMatch[1];
                      console.log('[FRONTEND-CLEAN] ✅ Matched placeholder format:', result);
                      return result;
                    }
                    
                    // Fallback: clean up the selector and make it readable
                    const result = cleaned
                      .replace(/[[\]"'=]/g, ' ')
                      .replace(/\s+/g, ' ')
                      .trim()
                      .replace(/_/g, ' ')
                      .replace(/-/g, ' ')
                      .replace(/\s+/g, ' ')
                      .trim();
                    
                    console.log('[FRONTEND-CLEAN] ⚠️ Using fallback cleanup:', result);
                    return result;
                  };
                  
                  // Handle "Click testid data testid X Y Z s" format directly from action text
                  if (originalAction.match(/Click\s+testid\s+data\s+testid\s+(.+?)\s+s$/i)) {
                    const match = originalAction.match(/Click\s+testid\s+data\s+testid\s+(.+?)\s+s$/i);
                    if (match) {
                      const cleanName = match[1].replace(/_/g, ' ').replace(/-/g, ' ').trim();
                      const result = `Click ${cleanName}`;
                      console.log('[FRONTEND-CLEAN] ✅ Fixed testid data testid format in action:', result);
                      return result;
                    }
                  }
                  
                  // Clean up click actions
                  if (originalAction.toLowerCase().includes('click')) {
                    // Extract selector from the action text or use provided selector
                    let selectorToClean = selector;
                    if (!selectorToClean) {
                      // Try to extract from action text
                      const match = originalAction.match(/click\s+(.+)/i);
                      if (match) {
                        selectorToClean = match[1].trim();
                      }
                    }
                    const cleanName = extractCleanName(selectorToClean);
                    const result = `Click ${cleanName}`;
                    console.log('[FRONTEND-CLEAN] ✅ Cleaned click action:', result);
                    return result;
                  }
                  
                  // Clean up fill actions
                  if (originalAction.toLowerCase().includes('fill')) {
                    const valueMatch = originalAction.match(/["']([^"']+)["']/);
                    const value = valueMatch ? valueMatch[1] : '';
                    let selectorToClean = selector;
                    if (!selectorToClean) {
                      const match = originalAction.match(/fill\s+([^"']+?)\s+with/i);
                      if (match) {
                        selectorToClean = match[1].trim();
                      }
                    }
                    const cleanName = extractCleanName(selectorToClean);
                    const lowerCleanName = cleanName.toLowerCase();
                    if (lowerCleanName.includes('email')) {
                      return `Fill "${value}" into the email field`;
                    } else if (lowerCleanName.includes('password')) {
                      return `Fill "${value}" into the password field`;
                    } else {
                      return `Fill "${value}" into the ${cleanName} field`;
                    }
                  }
                  
                  console.log('[FRONTEND-CLEAN] ⚠️ No cleaning applied, returning original');
                  return originalAction; // Fallback
                };
                
                // Apply cleaning to the action text
                actionText = cleanActionDescription(actionText, step.selector, step.code);
                
                // Try to extract better description from code if cleaning didn't help much
                if (step.code && (actionText.includes('testid data testid') || actionText.includes('internal:'))) {
                  if (step.code.includes('page.goto')) {
                    const urlMatch = step.code.match(/goto\(['"]([^'"]+)['"]/);
                    if (urlMatch) {
                      actionText = `Navigate to ${urlMatch[1]}`;
                    }
                  } else if (step.code.includes('.fill(')) {
                    const fillMatch = step.code.match(/locator\(['"]([^'"]+)['"]\)\.fill\(\s*(?:td\(\s*)?['"]([^'"]*)['"]/);
                    if (fillMatch) {
                      actionText = `Fill ${fillMatch[1]} with "${fillMatch[2]}"`;
                    } else {
                      // Fallback
                      if (step.selector) {
                        actionText = `Fill ${step.selector}`;
                      } else {
                        actionText = 'Fill';
                      }
                      if (step.value) {
                        actionText += ` with "${step.value}"`;
                      }
                    }
                  } else if (step.code.includes('.click()')) {
                    if (step.code.includes('getByText')) {
                      const textMatch = step.code.match(/getByText\(['"]([^'"]+)['"]/);
                      if (textMatch) {
                        actionText = `Click "${textMatch[1]}"`;
                      }
                    } else {
                      const selectorMatch = step.code.match(/locator\(['"]([^'"]+)['"]/);
                      if (selectorMatch) {
                        actionText = `Click ${selectorMatch[1]}`;
                      }
                    }
                  }
                }
                
                return {
                  id: step.step_number || index + 1,
                  action: actionText,
                  selector: step.selector,
                  value: step.value,
                  code: step.code,
                  actual: step.actual,
                  expected: step.expected,
                  dbId: step.id,
                  duration: 0,
                  status: 'completed'
                };
              });
              setTestSteps(steps);
              const testId = test?.dbId || test?.id || test?.test_case_id;
              console.log('Loaded', steps.length, 'steps from database for test', testId);
            } else {
              // No steps in database, clear steps
              setTestSteps([]);
            }
          }
        } catch (error) {
          console.error('Error loading test steps:', error);
        }
      };
      loadTestSteps();
    } else if (!test?.dbId) {
      // For non-db tests, don't clear steps if recording is active
      if (!isRecording) {
        // Only clear if not recording
      }
    }
  }, [test?.dbId, test?.id, test?.test_case_id, isRecording, test?.executionMode]);

  // Update browserUrl when test changes (but don't override proxy URL from backend)
  useEffect(() => {
    // During execution mode, never set browserUrl from test.url (backend will set it via testExecutionStarted)
    if (test?.executionMode) {
      return; // Don't set browserUrl from test.url during execution
    }
    
    // Only set initial URL if browserUrl is empty AND we haven't received a proxy URL yet
    // AND it's not viewport-viewer.html
    if (test?.url && !browserUrl && !test.url.includes('viewport-viewer.html')) {
      // Don't set proxy URL here - wait for recordingStarted message
      if (!browserUrl.includes('/proxy')) {
        setBrowserUrl(test.url);
      }
    }
  }, [test, browserUrl]);

  const addLog = useCallback((message, type = 'info') => {
    console.log(`[${type}]`, message);
  }, []);

  // Script injection is handled server-side by the proxy
  // No need for client-side injection - the proxy injects the script into HTML before serving it

  const handleMessage = useCallback((data) => {
    switch (data.type) {
      case 'recordingStarted':
        setIsRecording(true);
        // Don't clear steps - navigation step should already be there
        // Only clear if we're starting a completely new recording (no steps yet)
        setTestSteps(prev => {
          // If we already have steps (like navigation), keep them
          if (prev.length > 0) {
            console.log('[FRONTEND] Recording started - preserving existing steps:', prev.length);
            return prev;
          }
          // Otherwise, start fresh
          return [];
        });
        addLog(data.message || 'Recording started', 'info');
        
        // Use proxy URL for iframe embedding (real browser, not screenshots)
        if (data.cdpWsUrl || data.proxyUrl) {
          const proxyUrl = data.cdpWsUrl || data.proxyUrl;
          setCdpWsUrl(proxyUrl);
          console.log('[FRONTEND] Recording started with proxy URL for iframe:', proxyUrl);
        } else if (data.streamId) {
          // Fallback: old screenshot-based approach
          setStreamId(data.streamId);
          console.log('[FRONTEND] Recording started with streamId (screenshot mode):', data.streamId);
        } else {
          console.warn('[FRONTEND] Recording started but no proxy URL or streamId provided');
        }
        break;
      case 'browserProxyReady':
        // Real browser is ready via proxy - use iframe to embed
        if (data.proxyUrl) {
          // Only update if URL actually changed to prevent unnecessary re-renders
          setCdpWsUrl(prevUrl => {
            if (prevUrl !== data.proxyUrl) {
              console.log('[FRONTEND] Browser proxy ready, setting URL:', data.proxyUrl);
              addLog('Real browser embedded via proxy', 'info');
              
              // Clear viewport image since we're using iframe now
              setViewportImage(null);
              return data.proxyUrl;
            }
            return prevUrl; // No change, return previous value
          });
        }
        break;
      case 'viewportFrame':
        // Update browser viewport image (embedded browser view)
        if (data.image) {
          // Accept frame if streamId matches OR if we don't have a streamId yet (first frame)
          if (!streamId || data.streamId === streamId) {
            // If we don't have streamId yet, set it from the frame
            if (!streamId && data.streamId) {
              setStreamId(data.streamId);
              console.log('[FRONTEND] Setting streamId from first viewport frame:', data.streamId);
            }
            setViewportImage(data.image);
            if (data.frameNumber % 30 === 0) { // Log every 30 frames to reduce console spam
              console.log('[FRONTEND] Viewport frame received, streamId:', data.streamId, 'frame:', data.frameNumber);
            }
          } else {
            // Only log mismatch if we have a streamId set (to avoid spam)
            if (streamId) {
              console.warn('[FRONTEND] Viewport frame streamId mismatch. Expected:', streamId, 'Got:', data.streamId);
            }
          }
        }
        break;
      case 'recordingStopped':
        setIsRecording(false);
        setIsPaused(false); // Reset pause state
        setViewportImage(null); // Clear viewport image
        setStreamId(null); // Clear streamId
        addLog('Recording stopped', 'info');
        // Store the full test code for saving
        if (data.testCode) {
          setTestContent(data.testCode);
        }
        // Keep existing steps - they were added via stepRecorded events
        // Show save popup if test is not saved and has steps
        setTimeout(() => {
          setTestSteps(currentSteps => {
            if (!isSaved && currentSteps.length > 0) {
              setShowSaveConfirm(true);
            }
            return currentSteps;
          });
        }, 500);
        break;
      case 'recordingPaused':
        setIsPaused(true);
        addLog('Recording paused', 'info');
        break;
      case 'recordingResumed':
        setIsPaused(false);
        addLog('Recording resumed', 'info');
        break;
      // Any error while saving must release the button. It used to be cleared
      // only by 'testSaved', so a failed save left Save stuck on "Saving..."
      // for good, with the reason visible nowhere but the browser console.
      //
      // But "an error arrived while saving" is not the same as "the save
      // failed". Around twenty-five different places in the backend report
      // trouble down this one socket - a screenshot that timed out, a
      // recorder hiccup, a closed browser target - and any of them landing
      // during the save window used to be announced as "Save failed", while
      // the save itself had in fact succeeded. That is the error toast that
      // appeared even though the test was in the database.
      //
      // Only the save path tags its errors with context 'save', so only those
      // are reported as a failed save. Everything else is logged, as before.
      case 'error':
        if (isSavingRef.current && data.context === 'save') {
          setIsSaving(false);
          setShowMessage({
            show: true,
            message: data.message || 'Save failed. Please try again.',
            type: 'error'
          });
          setTimeout(() => setShowMessage({ show: false, message: '', type: 'success' }), 6000);
        }
        addLog(data.message || 'Error', 'error');
        break;

      case 'stepForgotten':
        // The recorder dropped the step too. Nothing more to do - the list was
        // already updated locally. Logged so the flow is traceable.
        addLog(data.ignored
          ? 'Step removed (no recording in progress)'
          : `Step removed from the recording (${data.stepCount} left)`, 'info');
        break;

      case 'testSaved':
        setIsSaving(false);
        setIsSaved(true);
        const successMsg = data.testCaseId 
          ? `Test ${data.isUpdate ? 'updated' : 'saved'} successfully! ID: ${data.testCaseId}, Steps: ${data.stepCount}`
          : `Test saved to file! Steps: ${data.stepCount}${data.warning ? ' (Note: ' + data.warning + ')' : ''}`;
        setSaveSuccessMessage(successMsg);
        addLog(successMsg, 'success');
        setShowSaveConfirm(false);
        // Update test with database ID if provided
        if (data.testCaseId && test) {
          // Update the test object with the database ID
          // Note: We can't directly setTest as test is a prop, but we can update local state
          console.log('Test saved with ID:', data.testCaseId);
        }
        // Refresh steps and code from backend so English panel and code popup stay in sync
        if (data.testCaseId) {
          (async () => {
            try {
              const resp = await fetch(`${API_BASE_URL}/api/test-cases/${data.testCaseId}`);
              const result = await resp.json();
              if (result.success && result.testCase) {
                if (result.testCase.content) {
                  setTestContent(result.testCase.content);
                  if (showCodePanel && !isEditingCode) {
                    setEditedCode(result.testCase.content);
                  }
                }
                if (Array.isArray(result.testCase.steps)) {
                  const refreshedSteps = result.testCase.steps.map((step, index) => ({
                    id: step.step_number || index + 1,
                    action: step.action || step.code || '',
                    selector: step.selector,
                    value: step.value,
                    code: step.code || step.action || '',
                    actual: step.actual,
                    expected: step.expected,
                    dbId: step.id,
                    duration: 0,
                    status: 'completed'
                  }));
                  setTestSteps(refreshedSteps);
                }
              }
            } catch (fetchErr) {
              console.error('Error refreshing test after save:', fetchErr);
            }
          })();
        }
        // Clear success message after 5 seconds
        setTimeout(() => {
          setSaveSuccessMessage(null);
        }, 5000);
        break;
      // viewportFrame removed - no iframe rendering
      case 'stepUpdated':
        // Step updated in real-time (e.g., fill value changed)
        if (data.step) {
          // Ignore updates while paused so paused typing doesn't mutate steps
          if (isPausedRef.current) break;
          console.log('[FRONTEND] Step updated received:', JSON.stringify(data.step, null, 2));
          setTestSteps(prev => {
            // Find step by ID and update it
            const stepIndex = prev.findIndex(s => s.id === data.step.id);
            if (stepIndex >= 0) {
              console.log('[FRONTEND] Updating existing step at index:', stepIndex, 'with new action:', data.step.action);
              const updatedSteps = [...prev];
              updatedSteps[stepIndex] = {
                ...updatedSteps[stepIndex],
                action: data.step.action,
                englishDescription: data.step.englishDescription || data.step.action,
                value: data.step.value,
                code: data.step.code,
                selector: data.step.selector
              };
              console.log('[FRONTEND] Step updated successfully. Total steps:', updatedSteps.length);
              return updatedSteps;
            } else {
              // Step not found - treat as new step (fallback for missed stepRecorded)
              console.warn('[FRONTEND] Step update received but step not found:', data.step.id, '- treating as new step');
              const newStep = {
                ...data.step,
                selector: data.step.selector || null,
                value: data.step.value || null,
                code: data.step.code || data.step.action,
                status: data.step.status || 'completed',
                englishDescription: data.step.englishDescription || data.step.action
              };
              console.log('[FRONTEND] Adding as new step:', newStep);
              return [...prev, newStep];
            }
          });
        }
        break;
      case 'stepRecorded':
        // New step recorded in real-time
        if (data.step) {
          // Ignore new steps while paused so typing during pause doesn't overwrite first step
          if (isPausedRef.current) break;

          // A recording left running for ANOTHER test keeps sending steps down
          // this same connection. Without this check they appeared in whichever
          // test happened to be open - continuing that recording's step numbers
          // and, worse, offering to save them into the wrong test.
          if (data.testId != null) {
            const openTestId = test?.dbId || test?.id || test?.test_case_id;
            if (openTestId != null && String(data.testId) !== String(openTestId)) {
              console.warn('[FRONTEND] Ignoring a step from a different test:', data.testId, 'open:', openTestId);
              break;
            }
          }
          console.log('[FRONTEND] Step recorded received:', JSON.stringify(data.step, null, 2));
          setTestSteps(prev => {
            console.log('[FRONTEND] Current steps before update:', prev.length);
            
            // Check if this is an update to the last fill step
            const currentLastFillIndex = lastFillStepIndexRef.current;
            if (data.isUpdate && currentLastFillIndex >= 0 && currentLastFillIndex < prev.length) {
              console.log('[FRONTEND] Updating existing fill step at index:', currentLastFillIndex);
              const updatedSteps = [...prev];
              updatedSteps[currentLastFillIndex] = {
                ...updatedSteps[currentLastFillIndex],
                ...data.step,
                selector: data.step.selector || updatedSteps[currentLastFillIndex].selector,
                value: data.step.value || updatedSteps[currentLastFillIndex].value,
                code: data.step.code || data.step.action || updatedSteps[currentLastFillIndex].code,
                duration: data.step.duration || updatedSteps[currentLastFillIndex].duration,
                status: data.step.status || updatedSteps[currentLastFillIndex].status
              };
              return updatedSteps;
            }
            
            // Check if step already exists (avoid duplicates) - but allow updates
            // IMPORTANT: Navigation step (ID=1) should always be preserved
            const existingIndex = prev.findIndex(s => s.id === data.step.id);
            if (existingIndex >= 0 && !data.isUpdate) {
              // If it's not an update and step exists, update it instead of skipping
              const existing = prev[existingIndex];
              if (existing.action === data.step.action && existing.selector === data.step.selector && existing.value === data.step.value) {
                console.log('[FRONTEND] Step already exists with same action/value, skipping:', data.step.id);
                return prev;
              }
              // If it's different, update it (might be a retry or correction)
              console.log('[FRONTEND] Step ID exists but differs, updating:', data.step.id);
              const updatedSteps = [...prev];
              updatedSteps[existingIndex] = {
                ...updatedSteps[existingIndex],
                ...data.step,
                selector: data.step.selector || updatedSteps[existingIndex].selector,
                value: data.step.value || updatedSteps[existingIndex].value,
                code: data.step.code || data.step.action || updatedSteps[existingIndex].code
              };
              return updatedSteps;
            }
            console.log('[FRONTEND] Adding new step to list:', data.step);
            // Ensure step has all required fields
            const newStep = {
              ...data.step,
              selector: data.step.selector || null,
              value: data.step.value || null,
              actual: data.step.actual || null,
              expected: data.step.expected || null,
              code: data.step.code || data.step.action,
              duration: data.step.duration || 0,
              status: data.step.status || 'completed'
            };
            
            // Check if this is a fill/type action - track it for potential updates
            if (newStep.action && (newStep.action.toLowerCase().includes('fill') || newStep.action.toLowerCase().includes('type'))) {
              const newIndex = prev.length; // This will be the index after adding
              lastFillStepIndexRef.current = newIndex; // Update ref
            }
            
            const newSteps = [...prev, newStep];
            console.log('[FRONTEND] Total steps after update:', newSteps.length);
            console.log('[FRONTEND] All steps:', newSteps.map(s => ({ id: s.id, action: s.action })));
            return newSteps;
          });
          addLog(`Step recorded: ${data.step.action}`, 'success');
        } else {
          console.warn('[FRONTEND] stepRecorded message received but no step data:', data);
        }
        break;
      case 'stdout':
        // Just log stdout, don't parse steps from here
        addLog(data.data, 'stdout');
        break;
      case 'stderr':
        addLog(data.data, 'stderr');
        break;
      default:
        break;
    }
    // Deliberate: re-creating this handler would tear down and re-attach the
    // WebSocket listener on every render. NOTE: streamId is read inside and
    // may be stale here - worth a proper look, not a blind dependency add.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addLog, isSaved, test]);

  // Try to connect to local recorder service (for headed browser on the user's machine)
  useEffect(() => {
    let socket = null;
    let retryTimer = null;

    const connect = () => {
      try {
        socket = new WebSocket(LOCAL_RECORDER_WS);

        socket.onopen = () => {
          console.log('[FRONTEND] Local recorder connected:', LOCAL_RECORDER_WS);
          setLocalServiceAvailable(true);
          setUseLocalService(true);
          setLocalWs(socket);
        };

        socket.onmessage = (event) => {
          try {
            const parsed = JSON.parse(event.data || '{}');
            handleMessage(parsed);
          } catch (err) {
            console.error('[FRONTEND] Local recorder message parse error:', err);
          }
        };

        socket.onclose = () => {
          console.log('[FRONTEND] Local recorder disconnected');
          setLocalServiceAvailable(false);
          setLocalWs(null);
          retryTimer = setTimeout(connect, 3000);
        };

        socket.onerror = (err) => {
          console.error('[FRONTEND] Local recorder socket error:', err);
        };
      } catch (err) {
        console.error('[FRONTEND] Local recorder connect failed:', err);
        retryTimer = setTimeout(connect, 3000);
      }
    };

    connect();

    return () => {
      if (retryTimer) clearTimeout(retryTimer);
      if (socket) socket.close();
    };
  }, [handleMessage]);

  // parseTestSteps removed - steps are now received directly from Playwright recorder via WebSocket

  // CRITICAL: Filter duplicates BEFORE displaying steps
  // This prevents duplicate steps from appearing in the UI
  useEffect(() => {
    if (!testSteps || testSteps.length === 0) {
      setDisplayedSteps([]);
      return;
    }

    // Normalize selector for comparison
    const normalizeSelector = (selector) => {
      if (!selector) return '';
      // Extract data-testid value
      const testIdMatch = selector.match(/\[data-testid=["']([^"']+)["']\]/);
      if (testIdMatch) return testIdMatch[1];
      // Extract name value
      const nameMatch = selector.match(/\[name=["']([^"']+)["']\]/);
      if (nameMatch) return nameMatch[1];
      // For OTP fields, keep full selector
      if (selector.match(/^#digit-\d+$/)) return selector;
      if (selector.match(/^#otp-\d+$/)) return selector;
      if (selector.match(/^#otp\d+$/)) return selector;
      // Remove # prefix for other selectors
      if (selector.startsWith('#')) return selector.substring(1);
      return selector;
    };

    // Extract value from step
    const extractValue = (step) => {
      if (step.value) return String(step.value).trim();
      // Extract from action text: "Fill email_address with "value""
      if (step.action) {
        const match = step.action.match(/with\s+["']([^"']+)["']/);
        if (match) return match[1].trim();
      }
      // Extract from code: .fill('value')
      if (step.code) {
        const match = step.code.match(/\.fill\(\s*(?:td\(\s*)?['"]([^'"]*)['"]/);
        if (match) return match[1].trim();
      }
      return '';
    };

    // Filter out duplicates (but keep OTP/digit fills intact)
    const seenFills = new Map(); // key: normalizedSelector_value, value: step index
    const filteredSteps = [];
    
    for (let i = 0; i < testSteps.length; i++) {
      const step = testSteps[i];
      const action = step.action || '';
      
      // For fill actions, check for duplicates
      if (action.toLowerCase().includes('fill')) {
        // A saved step has no stored selector - recover the target from its code,
        // otherwise every fill looks like the same element.
        const codeTarget =
          (step.code && (step.code.match(/locator\((['"])([\s\S]*?)\1\)/) || [])[2]) ||
          (step.code && (step.code.match(/getByTestId\((['"])([\s\S]*?)\1\)/) || [])[2]) ||
          '';
        const normalizedSelector = normalizeSelector(step.selector || codeTarget);
        const value = extractValue(step);
        const fillKey = `${normalizedSelector}_${value}`;
        const isOtp =
          normalizedSelector.toLowerCase().includes('digit-') ||
          normalizedSelector.toLowerCase().includes('otp') ||
          action.toLowerCase().includes('otp') ||
          (step.selector && /digit-\d+/i.test(step.selector)) ||
          (step.code && /#(?:digit|otp)-?\d+/i.test(step.code));
        
        // Check if we've seen this exact fill (same selector + same value)
        if (!isOtp && value && seenFills.has(fillKey)) {
          const previousIndex = seenFills.get(fillKey);
          const previousStep = testSteps[previousIndex];
          const timeDiff = step.timestamp ? (step.timestamp - (previousStep.timestamp || 0)) : 0;
          
          // Skip if duplicate within 30 seconds
          if (timeDiff < 30000) {
            console.log(`[FRONTEND] ⚠️ Filtering duplicate step: ${action} (selector: ${normalizedSelector}, value: "${value}", ${timeDiff}ms after previous)`);
            continue; // Skip this duplicate step
          }
        }
        
        // Track this fill
        if (value && !isOtp) {
          seenFills.set(fillKey, i);
        }
      }
      
      // Remember where this row really lives. The list on screen is filtered, so
      // its own index does NOT match testSteps - using it edited or deleted a
      // different step than the one clicked.
      filteredSteps.push({ ...step, originalIndex: i });
    }
    
    console.log(`[FRONTEND] Filtered steps: ${testSteps.length} → ${filteredSteps.length} (removed ${testSteps.length - filteredSteps.length} duplicates)`);
    setDisplayedSteps(filteredSteps);
  }, [testSteps]);

  // Keep code preview in sync with current steps (unless user is editing code manually)
  useEffect(() => {
    if (!testSteps || testSteps.length === 0) return;
    if (isEditingCode) return;

    const newCode = generateCodeFromSteps(testSteps);
    setTestContent(newCode);
    if (showCodePanel && !isEditingCode) {
      setEditedCode(newCode);
    }
  }, [testSteps, isEditingCode, showCodePanel, generateCodeFromSteps]);

  useEffect(() => {
    if (!ws) return;

    const messageHandler = (event) => {
      try {
        const data = JSON.parse(event.data);
        handleMessage(data);
      } catch (error) {
        console.error('Error parsing message:', error);
      }
    };

    ws.addEventListener('message', messageHandler);

    return () => {
      ws.removeEventListener('message', messageHandler);
    };
  }, [ws, handleMessage]);

  // Separate effect for postMessage from iframe
  useEffect(() => {
    const postMessageHandler = (event) => {
      // Log all messages for debugging
      console.log('[FRONTEND] Received postMessage:', event.data, 'from origin:', event.origin);
      
      // Handle step completion from iframe during execution mode
      if (event.data && event.data.type === 'stepCompleted') {
        console.log('[FRONTEND] ========== RECEIVED stepCompleted FROM IFRAME ==========');
        console.log('[FRONTEND] Step Index:', event.data.stepIndex);
        console.log('[FRONTEND] Success:', event.data.success);
        console.log('[FRONTEND] Error:', event.data.error);
        const { stepIndex, success, error } = event.data;
        
        // Send completion back to backend
        const sendCompletion = () => {
          if (sendMessage) {
            sendMessage('stepCompleted', { stepIndex, success, error });
            console.log('[FRONTEND] ✓ Sent stepCompleted to backend via sendMessage, stepIndex:', stepIndex);
          } else if (ws && ws.readyState === WebSocket.OPEN) {
            const message = { action: 'stepCompleted', payload: { stepIndex, success, error } };
            try {
              ws.send(JSON.stringify(message));
              console.log('[FRONTEND] ✓ Sent stepCompleted to backend via WebSocket, stepIndex:', stepIndex, 'success:', success);
            } catch (e) {
              console.error('[FRONTEND] Error sending stepCompleted via WebSocket:', e);
            }
          } else {
            console.error('[FRONTEND] Cannot send stepCompleted - no sendMessage or WebSocket not open, readyState:', ws?.readyState);
            // Retry after a short delay if WebSocket might reconnect
            if (ws && ws.readyState !== WebSocket.OPEN) {
              setTimeout(() => {
                if (ws && ws.readyState === WebSocket.OPEN) {
                  try {
                    ws.send(JSON.stringify({ action: 'stepCompleted', payload: { stepIndex, success, error } }));
                    console.log('[FRONTEND] ✓ Sent stepCompleted to backend via WebSocket (retry), stepIndex:', stepIndex);
                  } catch (e) {
                    console.error('[FRONTEND] Error on retry:', e);
                  }
                }
              }, 500);
            }
          }
        };
        
        // Send immediately
        sendCompletion();
        
        // For Navigate steps (stepIndex 0), send multiple times with delays (in case page is reloading)
        if (stepIndex === 0) {
          setTimeout(sendCompletion, 100);
          setTimeout(sendCompletion, 200);
          setTimeout(sendCompletion, 300);
          setTimeout(sendCompletion, 500);
        }
        return;
      }
      
      // Handle fill field completion confirmation
      if (event.data && event.data.type === 'fillFieldComplete') {
        console.log('[FRONTEND] Fill field completed in iframe:', event.data.selector, 'value:', event.data.value, 'success:', event.data.success, 'actualValue:', event.data.actualValue);
        
        // If fill was not successful, log warning
        if (!event.data.success) {
          console.warn('[FRONTEND] Fill failed:', event.data.error || 'Unknown error');
          // Don't clear pending fill if it failed - allow user to retry
          return;
        }
        
        // If value doesn't match, log warning
        if (event.data.actualValue !== event.data.value) {
          console.warn('[FRONTEND] Fill value mismatch - expected:', event.data.value, 'actual:', event.data.actualValue);
        }
        
        // If fill was successful, clear any pending fill for this selector to prevent dialog from showing again
        if (event.data.success && pendingFillRef.current && pendingFillRef.current.selector === event.data.selector) {
          console.log('[FRONTEND] Fill successful, clearing pending fill to prevent dialog loop');
          setPendingFill(null);
          pendingFillRef.current = null; // Update ref
          setShowFillDialog(false);
          showFillDialogRef.current = false; // Update ref
        }
        return;
      }
      
      // Handle viewport interactions from browser-stream iframe
      if (event.data && event.data.type === 'viewportInteraction') {
        console.log('[FRONTEND] Received viewportInteraction from iframe:', event.data);
        
        // If it's a text input (type action), show dialog instead of immediately forwarding
        if (event.data.action === 'type' && isRecording) {
          // Accumulate text input
          if (!showTextInputDialog && !pendingTextInput) {
            // First character - show dialog
            // Find the last fill/type step to update it
            setTestSteps(prev => {
              const lastFillIndex = prev.length - 1;
              if (lastFillIndex >= 0) {
                const lastStep = prev[lastFillIndex];
                if (lastStep && (lastStep.action?.toLowerCase().includes('fill') || lastStep.action?.toLowerCase().includes('type'))) {
                  lastFillStepIndexRef.current = lastFillIndex;
                }
              }
              return prev;
            });
            
            setPendingTextInput({ 
              x: event.data.x, 
              y: event.data.y, 
              text: event.data.text || '',
              streamId: event.data.streamId
            });
            setTextInputValue(event.data.text || '');
            setShowTextInputDialog(true);
          } else if (showTextInputDialog && pendingTextInput) {
            // Subsequent characters - append to existing text
            setTextInputValue(prev => prev + (event.data.text || ''));
          }
          return; // Don't forward yet - wait for Apply
        }
        
        // Forward other interactions to backend via WebSocket
        if (ws && ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({
            action: 'viewportInteraction',
            payload: event.data
          }));
        }
        return;
      }
      
      // Handle stepCompleted messages from iframe during execution
      if (event.data && event.data.type === 'stepCompleted') {
        const { stepIndex, success, error } = event.data;
        console.log('[FRONTEND] ========== RECEIVED stepCompleted FROM IFRAME ==========');
        console.log('[FRONTEND] stepIndex:', stepIndex);
        console.log('[FRONTEND] success:', success);
        console.log('[FRONTEND] error:', error);
        
        // Forward to backend via WebSocket
        if (sendMessage) {
          sendMessage('stepCompleted', { stepIndex, success, error });
          console.log('[FRONTEND] ✓ Forwarded stepCompleted to backend');
        } else if (ws && ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ 
            action: 'stepCompleted', 
            payload: { stepIndex, success, error } 
          }));
          console.log('[FRONTEND] ✓ Sent stepCompleted to backend via WebSocket');
        } else {
          console.error('[FRONTEND] Cannot send stepCompleted - WebSocket not available');
        }
        return;
      }
      
      // Only accept messages from our iframe
      if (event.data && event.data.type === 'recordStep') {
        const step = event.data.step;
        console.log('[FRONTEND] Received step from iframe:', step, 'isRecording:', isRecording);
        
        // Only process steps when recording is active
        if (!isRecording) {
          console.log('[FRONTEND] Ignoring step - recording not active');
          return;
        }
        
        // Forward fill actions immediately to backend for real-time recording
        // Backend will handle duplicate detection
        if (step.type === 'fill') {
          // Check if this selector was recently filled (within last 1 second) - prevent rapid duplicates
          if (recentlyFilledRef.current.has(step.selector)) {
            const timeSinceFill = Date.now() - (recentlyFilledRef.current.get(step.selector) || 0);
            if (timeSinceFill < 1000) {
              console.log('[FRONTEND] Ignoring rapid fill event for', step.selector, '- too soon after last fill');
              return;
            }
          }
          
          // Mark as recently filled with timestamp
          recentlyFilledRef.current.set(step.selector, Date.now());
          
          // Clear the timestamp after 2 seconds
          setTimeout(() => {
            recentlyFilledRef.current.delete(step.selector);
          }, 2000);
          
          console.log('[FRONTEND] Fill action detected - forwarding immediately to backend:', step.selector, '=', step.value);
          
          // Forward immediately to backend for real-time recording
          if (ws && ws.readyState === WebSocket.OPEN) {
            sendMessage('recordStep', { step: step });
            console.log('[FRONTEND] Fill step forwarded to backend:', step.type, step.selector);
          } else {
            console.warn('[FRONTEND] WebSocket not connected, cannot send fill step. State:', ws ? ws.readyState : 'null');
          }
        } else {
          // Forward other actions immediately to backend via WebSocket
          if (ws && ws.readyState === WebSocket.OPEN) {
            sendMessage('recordStep', { step: step });
            console.log('[FRONTEND] Step forwarded to backend:', step.type);
          } else {
            console.warn('[FRONTEND] WebSocket not connected, cannot send step. State:', ws ? ws.readyState : 'null');
          }
        }
      } else {
        // Suppress postMessage warnings - these are normal messages from other sources
        // Only log if it's a known error type
        if (event.data && event.data.type && ['error', 'warning'].includes(event.data.type)) {
          console.warn('[FRONTEND] Received error/warning postMessage:', event.data);
        }
      }
    };

    console.log('[FRONTEND] Setting up postMessage listener');
    window.addEventListener('message', postMessageHandler);
    return () => {
      console.log('[FRONTEND] Removing postMessage listener');
      window.removeEventListener('message', postMessageHandler);
    };
  }, [ws, sendMessage, isRecording, test?.executionMode, pendingTextInput, showTextInputDialog]);

  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [testSteps]);

  // Keep refs in sync with state (safety measure)
  useEffect(() => {
    showFillDialogRef.current = showFillDialog;
  }, [showFillDialog]);

  useEffect(() => {
    pendingFillRef.current = pendingFill;
  }, [pendingFill]);

  const handleStartRecording = useCallback((replaceExisting = false) => {
    // Extract original URL from browserUrl if it's a proxy URL or viewport viewer
    let urlToUse = browserUrl || test?.url;
    
    // If browserUrl is a proxy URL, extract the original target
    if (urlToUse && urlToUse.includes(`${API_BASE_URL}/proxy?target=`)) {
      try {
        const urlObj = new URL(urlToUse);
        const targetParam = urlObj.searchParams.get('target');
        if (targetParam) {
          urlToUse = decodeURIComponent(targetParam);
          console.log('[FRONTEND] Extracted original URL from proxy URL:', urlToUse);
        }
      } catch (e) {
        console.warn('[FRONTEND] Failed to extract URL from proxy:', e);
      }
    }
    
    // If browserUrl is viewport-viewer.html, use test?.url or originalUrl
    if (urlToUse && urlToUse.includes('viewport-viewer.html')) {
      urlToUse = test?.url || null;
      console.log('[FRONTEND] Using test URL instead of viewport viewer URL');
    }
    
    // Fallback to test?.url if we still don't have a valid URL
    if (!urlToUse || urlToUse.includes(`${API_BASE_URL}/proxy`) || urlToUse.includes('viewport-viewer.html')) {
      urlToUse = test?.url;
    }
    
    if (!urlToUse) {
      console.warn('No URL provided for recording');
      setShowMessage({ show: true, message: 'Please enter a URL to start recording', type: 'error' });
      setTimeout(() => setShowMessage({ show: false, message: '', type: 'success' }), 3000);
      return;
    }

    setIsSaved(false);
    setSaveSuccessMessage(null);

    // "Start fresh" has to clear the list on screen too, not just on the server.
    //
    // The choice was passed to the backend as replaceExisting, which correctly
    // began a new session - but this list was left holding the old steps, so
    // the newly recorded ones were appended to them and numbering carried on
    // from the end. It looked as though "Start fresh" had been ignored, and a
    // save made from that screen put the old steps back.
    if (replaceExisting) {
      setTestSteps([]);
      addLog('Starting fresh - previous steps cleared', 'info');
    }

    console.log('Starting new native recording');
    // Sanitize test name for file path
    const sanitizedName = test?.name 
      ? test.name.replace(/[^a-zA-Z0-9]/g, '_').replace(/\s+/g, '_')
      : 'test';
    const targetFile = `tests/${sanitizedName}.spec.ts`;
    
    console.log('Starting native recording with URL:', urlToUse);
    console.log('📁 Target file:', targetFile);
    console.log('WebSocket state:', ws.readyState);
    
    // Prefer local recorder if available
    if (useLocalService && localServiceAvailable && localWs && localWs.readyState === WebSocket.OPEN) {
      try {
        localWs.send(JSON.stringify({
          type: 'startRecording',
          payload: {
            url: urlToUse,
            testId: test?.id || test?.testCaseId || test?.dbId,
            applicationId: selectedApplication?.id || test?.application_id,
            testName: test?.name,
            target: targetFile,
            replaceExisting: !!replaceExisting, // same choice as the server path
          }
        }));
        setIsRecording(true);
        addLog(`Starting local recording for ${urlToUse}...`, 'info');
      } catch (err) {
        console.error('Error sending to local recorder:', err);
        setShowMessage({ show: true, message: 'Local recorder unavailable. Falling back to server.', type: 'error' });
        setTimeout(() => setShowMessage({ show: false, message: '', type: 'success' }), 3000);
      }
      return;
    }

    // Fallback to backend recorder
    if (!isConnected || !ws || ws.readyState !== WebSocket.OPEN) {
      console.error('WebSocket not connected');
      setShowMessage({ show: true, message: 'Not connected to backend. Please refresh the page.', type: 'error' });
      setTimeout(() => setShowMessage({ show: false, message: '', type: 'success' }), 3000);
      return;
    }

    sendMessage('startRecording', { 
      url: urlToUse, 
      target: targetFile,
      testId: test?.id || test?.testCaseId || test?.dbId, // Pass test ID for auto-save
      applicationId: selectedApplication?.id || test?.application_id, // Pass application ID for auto-save
      testName: test?.name, // Pass test name for saving
      replaceExisting: !!replaceExisting // start fresh instead of adding to the stored steps
    });
    
    addLog(`Starting server-side recording for ${urlToUse}...`, 'info');
  }, [browserUrl, test, sendMessage, isConnected, ws, addLog, selectedApplication, useLocalService, localServiceAvailable, localWs]);

  // Recording over a test that already has steps is ambiguous: the tester may be
  // resuming an interrupted recording, or re-recording from scratch. Guessing
  // "append" silently produced tests containing the same flow twice, so ask.
  const requestStartRecording = useCallback(() => {
    if ((testStepsRef.current || []).length > 0) {
      setRecordChoice({ count: (testStepsRef.current || []).length });
      return;
    }
    handleStartRecording(false);
  }, [handleStartRecording]);

  // Auto-start recording when component mounts with a test that has a URL
  useEffect(() => {
    // Only auto-start if we have all required conditions AND not in execution mode
    const shouldAutoStart = test && 
                            test.url && 
                            browserUrl && 
                            isConnected && 
                            !isRecording && 
                            !hasAutoStarted && 
                            !test?.executionMode &&
                            ws && 
                            ws.readyState === WebSocket.OPEN;
    
    if (shouldAutoStart) {
      console.log('Conditions met for auto-start:', {
        hasTest: !!test,
        hasUrl: !!test?.url,
        browserUrl,
        isConnected,
        isRecording,
        hasAutoStarted,
        wsReady: ws?.readyState
      });
      
      // Small delay to ensure everything is ready
      const timer = setTimeout(() => {
        setHasAutoStarted(true);
        // A test that ALREADY has steps must never start recording by itself:
        // the new actions are appended, so simply opening the test to look at
        // it, or to edit a step, quietly produced a test containing the flow
        // twice. Opening an empty test still starts recording, which is the
        // point of opening it.
        if ((testStepsRef.current || []).length > 0) {
          console.log('Not auto-starting: this test already has steps. Use Start Recording to choose.');
          addLog('This test already has steps - press Start Recording to add to them or start fresh.', 'info');
          return;
        }
        console.log('Auto-starting recording for test:', test.name, 'URL:', browserUrl);
        handleStartRecording(false);
      }, 1500); // Increased delay to ensure WebSocket is fully ready
      
      return () => clearTimeout(timer);
    }
  }, [test, browserUrl, isConnected, isRecording, hasAutoStarted, ws, handleStartRecording, addLog]);

  const handleStopRecording = () => {
    if (useLocalService && localServiceAvailable && localWs && localWs.readyState === WebSocket.OPEN) {
      try {
        localWs.send(JSON.stringify({ action: 'stopRecording' }));
      } catch (err) {
        console.error('Error sending stop to local recorder:', err);
      }
    } else {
      sendMessage('stopRecording');
    }
    // Steps will remain visible - they're not cleared
    setIsPaused(false); // Reset pause state when stopping
  };

  const handlePauseRecording = useCallback(() => {
    if (!isRecording) return;
    
    console.log('[FRONTEND] Pausing recording...');
    setIsPaused(true);
    isPausedRef.current = true;
    if (useLocalService && localServiceAvailable && localWs && localWs.readyState === WebSocket.OPEN) {
      // No-op for local service (not implemented)
    } else {
      sendMessage('pauseRecording');
    }
    addLog('Recording paused', 'info');
  }, [isRecording, sendMessage, addLog, useLocalService, localServiceAvailable, localWs]);

  const handleResumeRecording = useCallback(() => {
    if (!isRecording || !isPaused) return;
    
    console.log('[FRONTEND] Resuming recording...');
    setIsPaused(false);
    isPausedRef.current = false;
    if (useLocalService && localServiceAvailable && localWs && localWs.readyState === WebSocket.OPEN) {
      // No-op for local service (not implemented)
    } else {
      sendMessage('resumeRecording');
    }
    addLog('Recording resumed', 'info');
  }, [isRecording, isPaused, sendMessage, addLog, useLocalService, localServiceAvailable, localWs]);

  // A test exists server-side as soon as it has an id. `isSaved` only tracks
  // "saved during THIS session" - it starts false and is reset when a new
  // recording begins - so opening an already-saved test leaves it false. That
  // made the Improve names button vanish on exactly the tests it is most
  // useful for. Gate on the id instead, which is also what the handler needs.
  const savedTestId = test?.dbId || test?.id || test?.test_case_id;

  // Rewrite the step descriptions with an AI that reads the generated code.
  // The names in the list come from pattern-matching a selector, which is why
  // a step can read 'welcome to zoodpay admin panel'. This only rewrites the
  // wording - the code, selectors and values are untouched.
  const handleImproveNames = async () => {
    const testId = savedTestId;
    if (!testId || improvingNames) return;

    setImprovingNames(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/test-cases/${testId}/describe-steps`, { method: 'POST' });
      const data = await response.json();
      if (data.available) {
        await refreshStepsFromServer(testId);
        // Say what happened. A second press usually finds nothing to do, and
        // silence there looks identical to a broken button.
        setImproveResult(
          data.updated > 0
            ? `Renamed ${data.updated} step${data.updated === 1 ? '' : 's'}`
            : 'All step names already read well'
        );
      } else {
        setImproveResult(data.reason || 'Step names unchanged');
      }
    } catch (error) {
      setImproveResult('Could not reach the server');
    } finally {
      setImprovingNames(false);
      setTimeout(() => setImproveResult(''), 4000);
    }
  };

  const handleSave = () => {
    // Show confirmation dialog
    setShowSaveConfirm(true);
  };

  // Keep ref in sync so websocket handler reads latest pause state
  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);

  // Warn user if they try to leave/refresh while recording
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (isRecording) {
        event.preventDefault();
        event.returnValue = 'Recording in progress. Save & stop or discard before leaving?';
        setShowLeavePrompt(true);
        return event.returnValue;
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden && isRecording) {
        setShowLeavePrompt(true);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isRecording]);

  const confirmSave = () => {
    // Prevent multiple saves in quick succession
    if (isSaving) {
      setShowMessage({ show: true, message: 'Test is already being saved. Please wait...', type: 'warning' });
      setTimeout(() => setShowMessage({ show: false, message: '', type: 'success' }), 3000);
      setShowSaveConfirm(false);
      return;
    }

    if (isSaved) {
      setShowMessage({ show: true, message: 'Test has already been saved. It will be updated.', type: 'info' });
      setTimeout(() => setShowMessage({ show: false, message: '', type: 'success' }), 3000);
    }

    setIsSaving(true);
    setShowSaveConfirm(false);

    // Always use testContent if available (from backend), otherwise reconstruct from steps
    let contentToSave = testContent;
    
    if (!contentToSave && testSteps.length > 0) {
      // Reconstruct test code from steps
      const sanitizedName = test?.name 
        ? test.name.replace(/[^a-zA-Z0-9]/g, '_').replace(/\s+/g, '_')
        : 'test';
      
      contentToSave = `import { test, expect } from '@playwright/test';\n\ntest.use({ viewport: null, launchOptions: { args: ['--start-maximized'] } }); // open maximised; the page fills the window\n\n`;
      contentToSave += `test('${sanitizedName}', async ({ page }) => {\n`;
      contentToSave += '  test.setTimeout(120000); // whole-test budget\n  page.setDefaultTimeout(30000); // per-action budget: a broken click fails here, not at 120s\n\n';
      
      // Extract original URL
      let urlToUse = browserUrl || test?.url || '';
      if (urlToUse && urlToUse.includes('/proxy?target=')) {
        try {
          const urlMatch = urlToUse.match(/target=([^&]+)/);
          if (urlMatch) {
            urlToUse = decodeURIComponent(urlMatch[1]);
          }
        } catch (e) {
          // Use as is
        }
      }
      
      if (urlToUse && !urlToUse.includes('localhost:5000/proxy')) {
        contentToSave += `  await page.goto('${urlToUse}');\n`;
      }
      
      // Convert step actions to Playwright code
      testSteps.forEach(step => {
        const action = step.action;
        if (action.includes('Navigate to')) {
          const urlMatch = action.match(/Navigate to (.+)/);
          if (urlMatch && !urlMatch[1].includes('localhost:5000/proxy')) {
            contentToSave += `  await page.goto('${urlMatch[1].trim()}');\n`;
          }
        } else if (action.includes('Click')) {
          const textMatch = action.match(/Click "(.+)"/);
          if (textMatch) {
            contentToSave += `  await page.getByText('${textMatch[1].replace(/'/g, "\\'")}').click();\n`;
          } else {
            const selectorMatch = action.match(/Click (.+)/);
            if (selectorMatch && selectorMatch[1].startsWith('#')) {
              contentToSave += `  await page.locator('${selectorMatch[1]}').click();\n`;
            } else {
              contentToSave += `  await page.locator('${selectorMatch[1] || 'button'}').click();\n`;
            }
          }
        } else if (action.includes('Fill')) {
          const fillMatch = action.match(/Fill (.+?) with "(.+)"/);
          if (fillMatch) {
            const selector = fillMatch[1].trim();
            const value = fillMatch[2].replace(/'/g, "\\'");
            contentToSave += `  await page.locator('${selector}').fill('${value}');\n`;
          } else {
            const simpleMatch = action.match(/Fill (.+)/);
            if (simpleMatch) {
              contentToSave += `  await page.locator('${simpleMatch[1].trim()}').fill('');\n`;
            }
          }
        } else {
          // Generic action as comment
          contentToSave += `  // ${action}\n`;
        }
      });
      
      contentToSave += '});\n';
    }
    
    if (!contentToSave || testSteps.length === 0) {
      setShowMessage({ show: true, message: 'No test content to save. Please record some steps first.', type: 'error' });
      setTimeout(() => setShowMessage({ show: false, message: '', type: 'success' }), 3000);
      setShowSaveConfirm(false);
      return;
    }
    
    const sanitizedName = test?.name 
      ? test.name.replace(/[^a-zA-Z0-9]/g, '_').replace(/\s+/g, '_')
      : 'test';
    const filename = `tests/${sanitizedName}.spec.ts`;
    
    // Extract original URL
    let urlToSave = browserUrl || test?.url || '';
    if (urlToSave && urlToSave.includes('/proxy?target=')) {
      try {
        const urlMatch = urlToSave.match(/target=([^&]+)/);
        if (urlMatch) {
          urlToSave = decodeURIComponent(urlMatch[1]);
        }
      } catch (e) {
        // Use as is
      }
    }
    
    console.log('[FRONTEND] Saving test:', { filename, url: urlToSave, steps: testSteps.length, application_id: selectedApplication?.id });
    
    if (!selectedApplication) {
      setShowMessage({ show: true, message: 'Please select an application before saving the test.', type: 'error' });
      setTimeout(() => setShowMessage({ show: false, message: '', type: 'success' }), 3000);
      setShowSaveConfirm(false);
      return;
    }
    
    if (!sendMessage) {
      console.error('[FRONTEND] sendMessage is not available');
      setShowMessage({ show: true, message: 'Cannot save: WebSocket connection not available. Please refresh the page.', type: 'error' });
      setTimeout(() => setShowMessage({ show: false, message: '', type: 'success' }), 3000);
      setShowSaveConfirm(false);
      return;
    }
    
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      console.error('[FRONTEND] WebSocket not connected. State:', ws?.readyState);
      setShowMessage({ show: true, message: 'Cannot save: WebSocket not connected. Please wait for connection or refresh the page.', type: 'error' });
      setTimeout(() => setShowMessage({ show: false, message: '', type: 'success' }), 3000);
      setShowSaveConfirm(false);
      return;
    }
    
    console.log('[FRONTEND] Calling sendMessage with saveTest action');
    const testId = test?.dbId || test?.id || test?.test_case_id;
    const applicationId = selectedApplication?.id || test?.application_id;
    sendMessage('saveTest', {
      testId: testId,
      testName: test?.name || 'Recorded Test',
      applicationId: applicationId,
      url: browserUrl || test?.url
    });
    
    setIsSaving(true);
    // Note: isSaving will be reset when testSaved message is received
    // or when error message is received
  };

  const cancelSave = () => {
    setShowSaveConfirm(false);
  };

  const handleFillApply = () => {
    if (!pendingFill || !ws || ws.readyState !== WebSocket.OPEN) {
      console.error('[FRONTEND] Cannot apply fill - missing pendingFill or WebSocket not ready');
      return;
    }
    
    const selector = pendingFill.selector;
    const value = fillValue.trim(); // Use the value from the dialog input
    
    if (!value) {
      console.warn('[FRONTEND] Empty value, not filling');
      setShowFillDialog(false);
      setPendingFill(null);
      setFillValue('');
      return;
    }
    
    console.log('[FRONTEND] handleFillApply called with:', { selector, value });
    
    // CRITICAL: Close dialog IMMEDIATELY to prevent it from showing again
    setShowFillDialog(false);
    showFillDialogRef.current = false; // Update ref immediately
    const currentPendingFill = { ...pendingFill }; // Store copy before clearing
    setPendingFill(null);
    pendingFillRef.current = null; // Update ref
    setFillValue('');
    
    // Add selector to recently filled map to prevent immediate re-triggering
    recentlyFilledRef.current.set(selector, Date.now());
    console.log('[FRONTEND] Added', selector, 'to recently filled map (cooldown active)');
    
    // Clear the cooldown after 2 seconds
    if (fillCooldownRef.current) {
      clearTimeout(fillCooldownRef.current);
    }
    fillCooldownRef.current = setTimeout(() => {
      recentlyFilledRef.current.delete(selector);
      console.log('[FRONTEND] Removed', selector, 'from recently filled map (cooldown expired)');
    }, 2000);
    
    // First, send fill command to iframe via postMessage to actually fill the field
    const iframe = document.querySelector('.browser-iframe');
    if (!iframe || !iframe.contentWindow) {
      console.error('[FRONTEND] Iframe not found or not accessible');
      setShowMessage({ show: true, message: 'Error: Cannot access iframe. Please try again.', type: 'error' });
      setTimeout(() => setShowMessage({ show: false, message: '', type: 'success' }), 3000);
      return;
    }
    
    // Function to fill the field - will be called multiple times with retries
    const fillField = (attempt = 1) => {
      try {
        console.log(`[FRONTEND] Fill attempt ${attempt} for selector: ${selector}, value: ${value}`);
        
        // ALWAYS send postMessage first (this works for cross-origin)
        iframe.contentWindow.postMessage({
          type: 'fillField',
          selector: selector,
          value: value
        }, '*');
        console.log('[FRONTEND] Sent fillField postMessage to iframe');
        
        // Also try direct DOM access as fallback (for same-origin or proxied content)
        try {
          const iframeDoc = iframe.contentWindow.document;
          if (iframeDoc) {
            let element = null;
            
            // Try multiple selector strategies
            if (selector.startsWith('#')) {
              const id = selector.substring(1);
              // Try getElementById first (fastest)
              element = iframeDoc.getElementById(id);
              if (!element) {
                // Try querySelector with #
                element = iframeDoc.querySelector(selector);
              }
              if (!element) {
                // Try by name attribute
                element = iframeDoc.querySelector(`[name="${id}"]`);
              }
              if (!element) {
                // Try input with id
                element = iframeDoc.querySelector(`input#${id}`);
              }
            } else {
              element = iframeDoc.querySelector(selector);
            }
            
            if (element && (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA')) {
              console.log('[FRONTEND] Element found via direct access, filling...');
              
              // Focus first
              element.focus();
              
              // Clear existing value
              element.select();
              element.value = '';
              
              // Set value using native setter (for React)
              try {
                const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;
                nativeInputValueSetter.call(element, value);
              } catch (e) {
                element.value = value;
              }
              
              // Trigger events immediately with InputEvent (better for React)
              const inputEvent = new InputEvent('input', { 
                bubbles: true, 
                cancelable: true,
                data: value,
                inputType: 'insertText'
              });
              element.dispatchEvent(inputEvent);
              
              const changeEvent = new Event('change', { bubbles: true, cancelable: true });
              element.dispatchEvent(changeEvent);
              
              // Try React onChange if available
              try {
                const reactKey = Object.keys(element).find(key => key.startsWith('__reactInternalInstance') || key.startsWith('__reactFiber'));
                if (reactKey) {
                  const reactInstance = element[reactKey];
                  if (reactInstance && reactInstance.memoizedProps && reactInstance.memoizedProps.onChange) {
                    const syntheticEvent = {
                      target: element,
                      currentTarget: element,
                      bubbles: true,
                      cancelable: true,
                      defaultPrevented: false,
                      eventPhase: 2,
                      isTrusted: false,
                      nativeEvent: new Event('input'),
                      preventDefault: function() {},
                      stopPropagation: function() {},
                      type: 'change',
                      _programmatic: true
                    };
                    reactInstance.memoizedProps.onChange(syntheticEvent);
                    console.log('[FRONTEND] Triggered React onChange handler');
                  }
                }
              } catch (e) {
                // React not available
              }
              
              // Blur to trigger validation
              element.blur();
              
              // Verify and trigger again after delay
              setTimeout(() => {
                if (element.value !== value) {
                  console.warn('[FRONTEND] Value mismatch, retrying...');
                  try {
                    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;
                    nativeInputValueSetter.call(element, value);
                  } catch (e) {
                    element.value = value;
                  }
                  element.dispatchEvent(new InputEvent('input', { bubbles: true, cancelable: true, data: value, inputType: 'insertText' }));
                  element.dispatchEvent(new Event('change', { bubbles: true }));
                }
                console.log('[FRONTEND] Final value:', element.value);
              }, 100);
              
              console.log('[FRONTEND] Field filled via direct access');
            } else {
              console.log('[FRONTEND] Element not found via direct access, relying on postMessage');
            }
          }
        } catch (directError) {
          // Cross-origin or other error - postMessage should still work
          console.log('[FRONTEND] Direct access failed (expected for cross-origin), using postMessage:', directError.message);
        }
        
        // Retry if this is first attempt and we haven't confirmed success
        if (attempt === 1) {
          setTimeout(() => fillField(2), 200);
          setTimeout(() => fillField(3), 500);
        }
      } catch (error) {
        console.error('[FRONTEND] Error in fillField attempt', attempt, ':', error);
      }
    };
    
    // Start filling immediately
    fillField(1);
    
    // Update the fill value with user input and send to backend WITH fromDialog flag
    const updatedFill = {
      ...currentPendingFill,
      value: value,
      fromDialog: true
    };
    sendMessage('recordStep', { step: updatedFill });
    console.log('[FRONTEND] Fill step sent to backend (fromDialog=true):', value);
  };

  const handleFillCancel = () => {
    setShowFillDialog(false);
    showFillDialogRef.current = false; // Update ref
    setPendingFill(null);
    pendingFillRef.current = null; // Update ref
    setFillValue('');
    // Don't add to recently filled - user cancelled, so allow dialog to show again if needed
  };

  const handleTextInputApply = () => {
    if (!pendingTextInput || !ws || ws.readyState !== WebSocket.OPEN) {
      console.error('[FRONTEND] Cannot apply text input - missing pendingTextInput or WebSocket not ready');
      return;
    }
    
    const value = textInputValue.trim();
    
    if (!value) {
      console.warn('[FRONTEND] Empty value, not recording text input');
      setShowTextInputDialog(false);
      setPendingTextInput(null);
      setTextInputValue('');
      return;
    }
    
    console.log('[FRONTEND] handleTextInputApply called with:', { value });
    
    // Close dialog
    setShowTextInputDialog(false);
    const currentPendingTextInput = { ...pendingTextInput };
    setPendingTextInput(null);
    setTextInputValue('');
    
    // Update the existing step in the frontend first
    setTestSteps(prev => {
      // Find the last fill/type step to update
      let stepIndexToUpdate = lastFillStepIndexRef.current;
      if (stepIndexToUpdate < 0 || stepIndexToUpdate >= prev.length) {
        // Find the last fill step if index is invalid
        for (let i = prev.length - 1; i >= 0; i--) {
          const step = prev[i];
          if (step && (step.action?.toLowerCase().includes('fill') || step.action?.toLowerCase().includes('type'))) {
            stepIndexToUpdate = i;
            lastFillStepIndexRef.current = i;
            break;
          }
        }
      }
      
      if (stepIndexToUpdate >= 0 && stepIndexToUpdate < prev.length) {
        const updatedSteps = [...prev];
        const stepToUpdate = updatedSteps[stepIndexToUpdate];
        updatedSteps[stepIndexToUpdate] = {
          ...stepToUpdate,
          value: value,
          action: stepToUpdate.action?.replace(/with\s+"[^"]*"/, `with "${value}"`) || `Fill ${stepToUpdate.selector || 'field'} with "${value}"`,
          code: stepToUpdate.code?.replace(/fill\('.*?'\)/, `fill('${value}')`) || stepToUpdate.code?.replace(/\.type\(.*?\)/, `.type('${value}')`) || stepToUpdate.code
        };
        console.log('[FRONTEND] Updated step at index', stepIndexToUpdate, 'with value:', value);
        return updatedSteps;
      }
      return prev;
    });
    
    // Send the text input to backend - use stored selector from last click, NO click action
    if (ws && ws.readyState === WebSocket.OPEN) {
      // Send fill action directly using stored selector (from last click)
      // NO click action - the selector was already stored when user clicked
      console.log('[FRONTEND] Sending fill action with stored selector (no click):', value);
      
      // Send fill action with coordinates for precise targeting
      // This ensures we fill the exact element that was clicked, even if selector is ambiguous
      const fillX = typeof currentPendingTextInput.x === 'number' ? currentPendingTextInput.x : parseFloat(currentPendingTextInput.x);
      const fillY = typeof currentPendingTextInput.y === 'number' ? currentPendingTextInput.y : parseFloat(currentPendingTextInput.y);
      
      ws.send(JSON.stringify({
        action: 'viewportInteraction',
        payload: {
          action: 'fill',
          text: value,
          x: fillX, // Send coordinates for precise targeting
          y: fillY,
          streamId: currentPendingTextInput.streamId
        }
      }));
      console.log('[FRONTEND] Text input sent to backend (fill):', value);
    }
  };

  const handleTextInputCancel = () => {
    setShowTextInputDialog(false);
    setPendingTextInput(null);
    setTextInputValue('');
  };

  return (
    <div className="recording-view">
      {/* Fill Dialog */}
      {showFillDialog && pendingFill && (
        <div className="modal-overlay" onClick={handleFillCancel}>
          <div className="modal-content fill-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Fill Action</h3>
            <p>Enter the value to fill in the field:</p>
            <div className="fill-dialog-content">
              <div className="form-group">
                <label htmlFor="fill-selector">Selector:</label>
                <input
                  type="text"
                  id="fill-selector"
                  value={pendingFill.selector || ''}
                  readOnly
                  className="form-input readonly"
                />
              </div>
              <div className="form-group">
                <label htmlFor="fill-value">Value:</label>
                <input
                  type="text"
                  id="fill-value"
                  value={fillValue}
                  onChange={(e) => setFillValue(e.target.value)}
                  className="form-input"
                  placeholder="Enter value to fill"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleFillApply();
                    } else if (e.key === 'Escape') {
                      handleFillCancel();
                    }
                  }}
                />
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={handleFillCancel}>Cancel</button>
              <button className="btn-confirm" onClick={handleFillApply}>Apply</button>
            </div>
          </div>
        </div>
      )}

      {/* Text Input Dialog */}
      {showTextInputDialog && pendingTextInput && (
        <div className="modal-overlay" onClick={handleTextInputCancel}>
          <div className="modal-content fill-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Text Input</h3>
            <p>Enter the text to type:</p>
            <div className="fill-dialog-content">
              <div className="form-group">
                <label htmlFor="text-input-value">Text:</label>
                <input
                  type="text"
                  id="text-input-value"
                  value={textInputValue}
                  onChange={(e) => setTextInputValue(e.target.value)}
                  className="form-input"
                  placeholder="Enter text to type"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleTextInputApply();
                    } else if (e.key === 'Escape') {
                      handleTextInputCancel();
                    }
                  }}
                />
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={handleTextInputCancel}>Cancel</button>
              <button className="btn-confirm" onClick={handleTextInputApply}>Apply</button>
            </div>
          </div>
        </div>
      )}

      {/* Step Editor Dialog */}
      {showStepEditor && editingStep && (
        <div className="modal-overlay" onClick={() => { setShowStepEditor(false); setEditingStep(null); }}>
          <div className="modal-content step-editor" onClick={(e) => e.stopPropagation()}>
            <h3>Edit Step</h3>
            <div className="step-editor-form">
              <div className="form-group">
                <label htmlFor="step-action-type">Action Type:</label>
                <CustomDropdown
                  value={editingStep.actionType || 'click'}
                  onChange={(e) => setEditingStep({
                    ...editingStep,
                    actionType: e.target.value,
                    // The recorder captured the visible Upload button, but an upload has to
                    // target the file input. This default suits most pages.
                    selector: e.target.value === 'upload' && !editingStep.uploadName
                      ? 'input[type="file"]'
                      : editingStep.selector
                  })}
                  options={[
                    { value: 'click', label: 'Click' },
                    { value: 'fill', label: 'Fill' },
                    { value: 'navigate', label: 'Navigate' },
                    { value: 'assertVisible', label: 'Assert (element is visible)' },
                    { value: 'assert', label: 'Assert (exact text)' },
                    { value: 'assertContains', label: 'Assert (contains text)' },
                    { value: 'wait', label: 'Wait' },
                    { value: 'select', label: 'Select' },
                    { value: 'check', label: 'Check' },
                    { value: 'uncheck', label: 'Uncheck' },
                    { value: 'hover', label: 'Hover' },
                    { value: 'press', label: 'Press' },
                    { value: 'upload', label: 'Upload File' },
                    { value: 'custom', label: 'Custom Script (API call, etc.)' }
                  ]}
                  className="form-input"
                />
              </div>
              
              {(editingStep.actionType === 'fill' || editingStep.actionType === 'click' || editingStep.actionType === 'select' || editingStep.actionType === 'check' || editingStep.actionType === 'uncheck' || editingStep.actionType === 'hover' || editingStep.actionType === 'assertVisible') && (
                <div className="form-group">
                  <label htmlFor="step-selector">Selector:</label>
                  <input
                    type="text"
                    id="step-selector"
                    className="form-input"
                    value={editingStep.selector || ''}
                    onChange={(e) => setEditingStep({ ...editingStep, selector: e.target.value })}
                    placeholder="e.g., #email_address, .button, [data-testid='submit']"
                  />
                </div>
              )}
              
              {editingStep.actionType === 'fill' && (
                <div className="form-group">
                  <label htmlFor="step-value-type">Value type:</label>
                  <CustomDropdown
                    value={editingValueType}
                    onChange={(e) => {
                      const nextType = e.target.value;
                      const fields = VALUE_TYPE_FIELDS[nextType] || [];
                      const nextArgs = fields.map((f, i) => (editingTokenArgs[i] !== undefined ? editingTokenArgs[i] : f.def));
                      setEditingStep({
                        ...editingStep,
                        valueType: nextType,
                        tokenArgs: nextArgs,
                        // Switching back to static restores the recorded value.
                        value: nextType === 'static'
                          ? (editingStep.staticValue !== undefined ? editingStep.staticValue : '')
                          : buildValueToken(nextType, nextArgs),
                        staticValue: editingValueType === 'static'
                          ? (editingStep.value || '')
                          : editingStep.staticValue
                      });
                    }}
                    options={VALUE_TYPES}
                    className="form-input"
                  />
                  <small style={{ opacity: 0.7 }}>
                    Keep "Static text" to use the value exactly as recorded. Pick anything
                    else to get a fresh value on every run.
                  </small>
                </div>
              )}

              {((editingStep.actionType === 'fill' && editingValueType === 'static') || editingStep.actionType === 'select') && (
                <div className="form-group">
                  <label htmlFor="step-value">Value:</label>
                  <input
                    type="text"
                    id="step-value"
                    className="form-input"
                    value={editingStep.value || ''}
                    onChange={(e) => setEditingStep({ ...editingStep, value: e.target.value, staticValue: e.target.value })}
                    placeholder="Enter value"
                  />
                </div>
              )}

              {editingStep.actionType === 'fill' && editingValueType !== 'static' && (
                <>
                  {(VALUE_TYPE_FIELDS[editingValueType] || []).map((field, index) => (
                    <div className="form-group" key={field.key}>
                      <label htmlFor={`step-token-arg-${index}`}>{field.key}:</label>
                      <input
                        type="text"
                        id={`step-token-arg-${index}`}
                        className="form-input"
                        value={editingTokenArgs[index] !== undefined ? editingTokenArgs[index] : field.def}
                        placeholder={field.def}
                        onChange={(e) => {
                          const nextArgs = [...editingTokenArgs];
                          nextArgs[index] = e.target.value;
                          setEditingStep({
                            ...editingStep,
                            valueType: editingValueType,
                            tokenArgs: nextArgs,
                            value: buildValueToken(editingValueType, nextArgs)
                          });
                        }}
                      />
                    </div>
                  ))}
                  <div className="form-group">
                    <label>Example value:</label>
                    <div style={{
                      fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
                      fontSize: '0.8125rem',
                      background: '#F9FAFB',
                      border: '1px solid #E5E7EB',
                      borderRadius: '6px',
                      padding: '0.625rem 0.75rem',
                      color: '#374151',
                      overflowWrap: 'anywhere'
                    }}>
                      {previewValueToken(editingValueType, editingTokenArgs) || '\u2014'}
                    </div>
                    <small style={{ opacity: 0.7 }}>
                      An example only &mdash; the real value is generated fresh each run.
                    </small>
                  </div>
                </>
              )}

              {editingStep.actionType === 'wait' && (
                <div className="form-group">
                  <label htmlFor="step-wait">Timeout (ms):</label>
                  <input
                    type="number"
                    id="step-wait"
                    className="form-input"
                    value={editingStep.value || ''}
                    onChange={(e) => setEditingStep({ ...editingStep, value: e.target.value })}
                    placeholder="1000"
                    min="0"
                  />
                </div>
              )}
              
              {editingStep.actionType === 'navigate' && (
                <div className="form-group">
                  <label htmlFor="step-url">URL:</label>
                  <input
                    type="text"
                    id="step-url"
                    className="form-input"
                    style={{ width: '100%' }}
                    value={editingStep.value || editingStep.url || ''}
                    onChange={(e) => setEditingStep({ ...editingStep, value: e.target.value, url: e.target.value })}
                    placeholder="https://example.com"
                  />
                </div>
              )}
              
              {editingStep.actionType === 'upload' && (
                <>
                  <div className="form-group">
                    <label htmlFor="step-upload-button">Upload button (recommended):</label>
                    <input
                      type="text"
                      id="step-upload-button"
                      className="form-input"
                      value={editingStep.uploadButton || ''}
                      onChange={(e) => setEditingStep({ ...editingStep, uploadButton: e.target.value })}
                      placeholder={'[data-testid="upload-btn"]'}
                    />
                    <small style={{ opacity: 0.7 }}>
                      The button you actually click. Fill this in and the step clicks it and
                      answers the file dialog the browser opens - so it does not matter how
                      many hidden file inputs the page has, or where they are. Leave it empty
                      to address the file input directly below.
                    </small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="step-upload-selector">File input selector:</label>
                    <input
                      type="text"
                      id="step-upload-selector"
                      className="form-input"
                      value={editingStep.selector || ''}
                      onChange={(e) => setEditingStep({ ...editingStep, selector: e.target.value })}
                      placeholder={'input[type="file"]'}
                      disabled={!!editingStep.uploadButton}
                    />
                    <small style={{ opacity: 0.7 }}>
                      {editingStep.uploadButton
                        ? 'Not used while an upload button is set.'
                        : 'The hidden file input, not the visible Upload button. input[type="file"] on its own fails when the page has more than one upload - narrow it, for example input[aria-label="Upload profile picture"].'}
                    </small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="step-upload-picker">Choose a file:</label>
                    <input
                      type="file"
                      id="step-upload-picker"
                      className="form-input"
                      onChange={(e) => {
                        const file = e.target.files && e.target.files[0];
                        if (!file) return;
                        if (file.size > 1024 * 1024) {
                          setShowMessage({ show: true, type: 'error', message:
                            'That file is larger than 1 MB. Large files are better referenced by path than carried inside the test.' });
                          setTimeout(() => setShowMessage({ show: false, message: '', type: 'success' }), 5000);
                          return;
                        }
                        // Read it here in the browser - nothing is sent to any server.
                        const isText = /^text\//.test(file.type) || /\.(csv|txt|json|xml|html?)$/i.test(file.name);
                        const reader = new FileReader();
                        reader.onload = () => {
                          const raw = String(reader.result || '');
                          setEditingStep(prev => ({
                            ...prev,
                            uploadName: file.name,
                            uploadMime: file.type || (isText ? 'text/plain' : 'application/octet-stream'),
                            uploadIsBase64: !isText,
                            uploadContent: isText ? raw : raw.split(',').pop()
                          }));
                        };
                        if (isText) reader.readAsText(file); else reader.readAsDataURL(file);
                      }}
                    />
                    <small style={{ opacity: 0.7 }}>
                      Read in your browser and stored inside the test, so it runs anywhere -
                      no shared folder or server access needed.
                    </small>
                  </div>

                  {editingStep.uploadName && (
                    <div className="form-group">
                      <label htmlFor="step-upload-name">File name sent to the page:</label>
                      <input
                        type="text"
                        id="step-upload-name"
                        className="form-input"
                        value={editingStep.uploadName || ''}
                        onChange={(e) => setEditingStep({ ...editingStep, uploadName: e.target.value })}
                      />
                    </div>
                  )}

                  {editingStep.uploadName && !editingStep.uploadIsBase64 && (
                    <div className="form-group">
                      <label htmlFor="step-upload-content">File contents (editable):</label>
                      <textarea
                        id="step-upload-content"
                        className="form-input"
                        rows={8}
                        style={{ fontFamily: 'monospace', fontSize: '0.85rem', whiteSpace: 'pre' }}
                        value={editingStep.uploadContent || ''}
                        onChange={(e) => setEditingStep({ ...editingStep, uploadContent: e.target.value })}
                      />
                      <small style={{ opacity: 0.7 }}>
                        Edit the data here for the next run - no need to produce a new file.
                      </small>
                    </div>
                  )}

                  {editingStep.uploadIsBase64 && (
                    <div className="form-group">
                      <small style={{ opacity: 0.7 }}>
                        Binary file held inside the test ({Math.round((editingStep.uploadContent || '').length * 0.75 / 1024)} KB).
                      </small>
                    </div>
                  )}

                  {!editingStep.uploadName && (
                    <div className="form-group">
                      <label htmlFor="step-upload-file">...or a file path on the machine that runs the test:</label>
                      <input
                        type="text"
                        id="step-upload-file"
                        className="form-input"
                        value={editingStep.value || ''}
                        onChange={(e) => setEditingStep({ ...editingStep, value: e.target.value })}
                        placeholder="tests/fixtures/data.csv"
                      />
                      <small style={{ opacity: 0.7 }}>
                        Only for files too large to carry inside the test. The file must exist
                        wherever the test runs.
                      </small>
                    </div>
                  )}
                </>
              )}

              {editingStep.actionType === 'custom' && (
                <div className="form-group">
                  <label htmlFor="step-script">Script:</label>
                  <textarea
                    id="step-script"
                    className="form-input"
                    rows={8}
                    style={{ fontFamily: 'monospace', fontSize: '0.85rem', whiteSpace: 'pre' }}
                    value={editingStep.script || ''}
                    onChange={(e) => setEditingStep({ ...editingStep, script: e.target.value })}
                    placeholder={"// Runs as part of the test. Use page.request so the call\n// shares the browser's logged-in session:\n\nconst res = await page.request.get('https://api.example.com/orders');\nexpect(res.ok()).toBeTruthy();\nconst body = await res.json();\nexpect(body.status).toBe('active');"}
                  />
                  <small style={{ opacity: 0.7 }}>
                    Written into the test exactly as typed. `page` and `expect` are available.
                  </small>
                </div>
              )}

              {(editingStep.actionType === 'assert' || editingStep.actionType === 'assertContains') && (
                <>
                  <div className="form-group">
                    <label htmlFor="step-selector">Selector:</label>
                    <input
                      type="text"
                      id="step-selector"
                      className="form-input"
                      value={editingStep.selector || ''}
                      onChange={(e) => setEditingStep({ ...editingStep, selector: e.target.value })}
                      placeholder="Element selector"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="step-expected">Expected Value:</label>
                    <input
                      type="text"
                      id="step-expected"
                      className="form-input"
                      value={editingStep.expected || ''}
                      onChange={(e) => setEditingStep({ ...editingStep, expected: e.target.value })}
                      placeholder="Expected value"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="step-actual">Actual Value:</label>
                    <input
                      type="text"
                      id="step-actual"
                      className="form-input"
                      value={editingStep.actual || ''}
                      onChange={(e) => setEditingStep({ ...editingStep, actual: e.target.value })}
                      placeholder="Actual value (optional)"
                    />
                  </div>
                </>
              )}
              
              <div className="form-group">
                {/* <label htmlFor="step-action-text">Action Description:</label>
                <input
                  type="text"
                  id="step-action-text"
                  className="form-input"
                  value={editingStep.action || ''}
                  onChange={(e) => setEditingStep({ ...editingStep, action: e.target.value })}
                  placeholder="e.g., Click #submit-button"
                /> */}
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => { setShowStepEditor(false); setEditingStep(null); }}>Cancel</button>
              <button className="btn-confirm" onClick={async () => {
                const updatedStep = { ...editingStep };
                
                // Always rebuild action text to keep English step in sync
                if (updatedStep.actionType === 'navigate') {
                  updatedStep.action = `Navigate to ${updatedStep.value || updatedStep.url || ''}`;
                } else if (updatedStep.actionType === 'fill') {
                  const fieldName = updatedStep.selector ? 
                    updatedStep.selector.replace(/[#.[\]"'=]/g, ' ').trim().replace(/\s+/g, ' ') : 
                    'field';
                  updatedStep.action = `Fill "${updatedStep.value || ''}" into the ${fieldName} field`;
                } else if (updatedStep.actionType === 'upload') {
                  const uploadLabel = updatedStep.uploadName || updatedStep.value || '';
                  const viaButton = String(updatedStep.uploadButton || '').trim();
                  updatedStep.action = viaButton
                    ? `Upload "${uploadLabel}" using ${viaButton}`
                    : `Upload "${uploadLabel}" into ${updatedStep.selector || 'the file input'}`;
                } else if (updatedStep.actionType === 'custom') {
                  const firstLine = String(updatedStep.script || '').split('\n').map(l => l.trim()).filter(Boolean)[0] || '';
                  updatedStep.action = firstLine ? `Custom script: ${firstLine.slice(0, 60)}` : 'Custom script';
                } else if (updatedStep.actionType === 'assertVisible') {
                  updatedStep.action = `Verify ${updatedStep.selector || 'element'} is visible`;
                } else if (updatedStep.actionType === 'assertContains') {
                  updatedStep.action = `Verify "${updatedStep.expected || ''}" appears in ${updatedStep.selector || 'element'}`;
                } else if (updatedStep.actionType === 'assert') {
                  updatedStep.action = `Verify ${updatedStep.selector || 'element'} has exactly the text "${updatedStep.expected || ''}"`;
                } else if (updatedStep.actionType === 'click') {
                  const elementName = updatedStep.selector ? 
                    updatedStep.selector.replace(/[#.[\]"'=]/g, ' ').trim().replace(/\s+/g, ' ') : 
                    'element';
                  updatedStep.action = `Click ${elementName}`;
                } else if (updatedStep.actionType === 'fill') {
                  const info = parseValueToken(updatedStep.value);
                  const typeLabel = (VALUE_TYPES.find(t => t.value === info.valueType) || {}).label;
                  updatedStep.action = info.valueType === 'static'
                    ? `Fill "${updatedStep.selector || 'field'}" with "${updatedStep.value || ''}"`
                    : `Fill "${updatedStep.selector || 'field'}" with ${String(typeLabel || info.valueType).toLowerCase()}`;
                } else if (updatedStep.actionType === 'select') {
                  updatedStep.action = `Select "${updatedStep.value || ''}" in ${updatedStep.selector || 'element'}`;
                } else if (updatedStep.actionType === 'check') {
                  updatedStep.action = `Check ${updatedStep.selector || 'checkbox'}`;
                } else if (updatedStep.actionType === 'uncheck') {
                  updatedStep.action = `Uncheck ${updatedStep.selector || 'checkbox'}`;
                } else if (updatedStep.actionType === 'hover') {
                  updatedStep.action = `Hover over ${updatedStep.selector || 'element'}`;
                } else if (updatedStep.actionType === 'press') {
                  updatedStep.action = `Press "${updatedStep.value || 'Enter'}"`;
                } else if (updatedStep.actionType === 'wait') {
                  updatedStep.action = `Wait for ${updatedStep.value || '1000'} ms`;
                } else {
                  updatedStep.action = `${updatedStep.actionType || 'Click'} ${updatedStep.selector || ''}`;
                }
                updatedStep.englishDescription = updatedStep.action;
                
                // Build code
                if (updatedStep.actionType === 'navigate') {
                  updatedStep.code = `await page.goto('${updatedStep.value || updatedStep.url || ''}');`;
                } else if (updatedStep.actionType === 'fill') {
                  // A token goes through td() so it resolves at run time; a
                  // static value is written exactly as it always was.
                  const fillValue = updatedStep.value || '';
                  const fillIsToken = parseValueToken(fillValue).valueType !== 'static';
                  const fillEscaped = String(fillValue).replace(/\\/g, '\\\\').replace(/'/g, "\\'");
                  updatedStep.code = fillIsToken
                    ? `await ${assertTarget(updatedStep)}.fill(td('${fillEscaped}'));`
                    : `await ${assertTarget(updatedStep)}.fill('${fillEscaped}');`;
                } else if (updatedStep.actionType === 'click') {
                  updatedStep.code = `await ${assertTarget(updatedStep)}.click();`;
                } else if (updatedStep.actionType === 'upload') {
                  const uploadSelector = updatedStep.selector || 'input[type="file"]';
                  const uploadButton = String(updatedStep.uploadButton || '').trim();
                  // Kept on ONE line: saved code is read back line by line, and a call
                  // split across lines would be torn into separate steps.
                  const escaped = String(updatedStep.uploadContent || '')
                    .replace(/\\/g, '\\\\')
                    .replace(/'/g, "\\'")
                    .replace(/\r/g, '\\r')
                    .replace(/\n/g, '\\n');
                  const encoding = updatedStep.uploadIsBase64 ? ", 'base64'" : '';
                  const files = updatedStep.uploadName
                    ? `{ name: '${updatedStep.uploadName}', mimeType: '${updatedStep.uploadMime || 'text/plain'}', buffer: Buffer.from('${escaped}'${encoding}) }`
                    : `'${updatedStep.value || ''}'`;

                  if (uploadButton) {
                    // Drive the upload through the button the tester actually clicks.
                    //
                    // The visible button usually carries a data-testid; the real
                    // <input type="file"> behind it rarely carries anything, so
                    // input[type="file"] is the only thing left to aim at - and the
                    // moment a page has two uploads that matches both and Playwright
                    // refuses to guess ("strict mode violation").
                    //
                    // Clicking the button and catching the file dialog the browser
                    // opens sidesteps the inputs entirely: it does not matter how many
                    // there are, whether they are hidden, or whether the page creates
                    // one on the fly. Wrapped as a custom step so the four lines stay
                    // together as a single step when the code is read back.
                    updatedStep.code = [
                      '// @custom-step',
                      'const fileChooserPromise = page.waitForEvent(\'filechooser\');',
                      `await page.locator('${uploadButton}').click();`,
                      'const fileChooser = await fileChooserPromise;',
                      `await fileChooser.setFiles(${files});`,
                      '// @end-custom-step'
                    ].join('\n  ');
                  } else if (updatedStep.uploadName) {
                    updatedStep.code = `await page.locator('${uploadSelector}').setInputFiles(${files});`;
                  } else {
                    updatedStep.code = `await page.locator('${uploadSelector}').setInputFiles('${updatedStep.value || ''}');`;
                  }
                } else if (updatedStep.actionType === 'custom') {
                  const body = String(updatedStep.script || '')
                    .split('\n')
                    .map(l => l.trim())
                    .filter(Boolean);
                  updatedStep.code = ['// @custom-step', ...body, '// @end-custom-step'].join('\n  ');
                } else if (updatedStep.actionType === 'assertVisible') {
                  updatedStep.code = `await expect(${assertTarget(updatedStep)}).toBeVisible({ timeout: 30000 });`;
                } else if (updatedStep.actionType === 'assertContains') {
                  updatedStep.code = `await expect(${assertTarget(updatedStep)}).toContainText('${updatedStep.expected || ''}', { timeout: 30000 });`;
                  updatedStep.value = updatedStep.value || updatedStep.expected || '';
                } else if (updatedStep.actionType === 'assert') {
                  updatedStep.code = `await expect(${assertTarget(updatedStep)}).toHaveText('${updatedStep.expected || ''}', { timeout: 30000 });`;
                  updatedStep.value = updatedStep.value || updatedStep.expected || '';
                } else if (updatedStep.actionType === 'select') {
                  updatedStep.code = `await ${assertTarget(updatedStep)}.selectOption('${updatedStep.value || ''}');`;
                } else if (updatedStep.actionType === 'check') {
                  updatedStep.code = `await ${assertTarget(updatedStep)}.check();`;
                } else if (updatedStep.actionType === 'uncheck') {
                  updatedStep.code = `await ${assertTarget(updatedStep)}.uncheck();`;
                } else if (updatedStep.actionType === 'hover') {
                  updatedStep.code = `await ${assertTarget(updatedStep)}.hover();`;
                } else if (updatedStep.actionType === 'press') {
                  updatedStep.code = `await page.keyboard.press('${updatedStep.value || 'Enter'}');`;
                } else if (updatedStep.actionType === 'wait') {
                  updatedStep.code = `await page.waitForTimeout(${updatedStep.value || '1000'});`;
                } else {
                  updatedStep.code = `await ${assertTarget(updatedStep)}.${updatedStep.actionType || 'click'}();`;
                }
                
                const testId = test?.dbId || test?.id || test?.test_case_id;

                let newCodeForSave = null;

                // Build the updated step list and its code SYNCHRONOUSLY.
                // This used to happen inside a setTestSteps(prev => ...) updater,
                // which React does not run immediately - so newCodeForSave was
                // still null when the save request below read it, and the old
                // code got saved instead, silently discarding this edit.
                const newSteps = testSteps.map((s, i) =>
                  i === editingStep.index ? { ...updatedStep, id: s.id } : s
                );

                const testName = test?.name || 'recorded test';
                let newTestCode = `import { test, expect } from '@playwright/test';\n\ntest.use({ viewport: null, launchOptions: { args: ['--start-maximized'] } }); // open maximised; the page fills the window\n\n`;
                newTestCode += `test('${testName}', async ({ page }) => {\n`;
                newTestCode += '  test.setTimeout(120000); // whole-test budget\n  page.setDefaultTimeout(30000); // per-action budget: a broken click fails here, not at 120s\n\n';

                for (const step of newSteps) {
                  if (step.code && !step.code.includes('localhost:5000')) {
                    newTestCode += '  ' + step.code.trim() + '\n';
                  }
                }

                newTestCode += '});\n';
                newCodeForSave = newTestCode;

                setTestSteps(newSteps);
                setTestContent(newTestCode);

                // Update edited code if code panel is open
                if (showCodePanel && !isEditingCode) {
                  setEditedCode(newTestCode);
                }

                // Persist the single step to DB if it exists there
                if (updatedStep.dbId) {
                  try {
                    const response = await fetch(`${API_BASE_URL}/api/steps/${updatedStep.dbId}`, {
                      method: 'PUT',
                      headers: {
                        'Content-Type': 'application/json',
                      },
                      body: JSON.stringify({
                        action: updatedStep.action,
                        selector: updatedStep.selector,
                        value: updatedStep.value,
                        code: updatedStep.code,
                        actual: updatedStep.actual,
                        expected: updatedStep.expected,
                      }),
                    });
                    const data = await response.json();
                    if (!data.success) {
                      setShowMessage({ show: true, message: 'Error updating step: ' + data.error, type: 'error' });
                      setTimeout(() => setShowMessage({ show: false, message: '', type: 'success' }), 3000);
                    }
                  } catch (error) {
                    console.error('Error updating step:', error);
                    setShowMessage({ show: true, message: 'Error updating step', type: 'error' });
                    setTimeout(() => setShowMessage({ show: false, message: '', type: 'success' }), 3000);
                  }
                }

                // If we have a testId, update the full code in DB/file to keep in sync
                if (testId) {
                  try {
                    const response = await fetch(`${API_BASE_URL}/api/test-cases/${testId}/code`, {
                      method: 'PUT',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ content: newCodeForSave || testContent || '' })
                    });
                    const data = await response.json();
                    if (!data.success) {
                      console.warn('Test code update failed after step edit:', data.error);
                    } else {
                      await refreshStepsFromServer(testId, data.steps);
                    }
                  } catch (err) {
                    console.warn('Error updating test code after step edit:', err);
                  }
                }

                setShowStepEditor(false);
                setEditingStep(null);
                setShowMessage({ show: true, message: 'Step updated successfully!', type: 'success' });
                setTimeout(() => setShowMessage({ show: false, message: '', type: 'success' }), 3000);
              }}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Save Confirmation Dialog */}
      {recordChoice && (
        <div className="modal-overlay" onClick={() => setRecordChoice(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <div className="modal-header">
              <h3>This test already has {recordChoice.count} step{recordChoice.count === 1 ? '' : 's'}</h3>
            </div>
            <div className="modal-body">
              <p style={{ marginTop: 0 }}>What should the new recording do with them?</p>
              <p style={{ fontSize: '0.875rem', color: '#6B7280', marginBottom: 0 }}>
                <strong>Add to the end</strong> continues where the test left off &mdash; use this to
                carry on an interrupted recording.<br />
                <strong>Start fresh</strong> replaces the {recordChoice.count} stored step{recordChoice.count === 1 ? '' : 's'} with
                whatever you record now.
              </p>
            </div>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setRecordChoice(null)}>Cancel</button>
              <button
                className="btn-cancel"
                onClick={() => { setRecordChoice(null); handleStartRecording(false); }}
              >
                Add to the end
              </button>
              <button
                className="btn-confirm"
                onClick={() => { setRecordChoice(null); handleStartRecording(true); }}
              >
                Start fresh
              </button>
            </div>
          </div>
        </div>
      )}

      {showSaveConfirm && (
        <div className="modal-overlay" onClick={cancelSave}>
          <div className="modal-content-save" onClick={(e) => e.stopPropagation()}>
            <h3>Confirm Save</h3>
            <p>Are you sure you want to save this test?</p>
            <div className="save-info">
              <p><strong>Test Name:</strong> {test?.name || 'Untitled test'}</p>
              <p><strong>Steps:</strong> {testSteps.length}</p>
              <p><strong>URL:</strong> {(() => {
                let urlToShow = browserUrl || test?.url || 'N/A';
                if (urlToShow.includes('/proxy?target=')) {
                  try {
                    const urlMatch = urlToShow.match(/target=([^&]+)/);
                    if (urlMatch) {
                      urlToShow = decodeURIComponent(urlMatch[1]);
                    }
                  } catch (e) {
                    // Use as is
                  }
                }
                return urlToShow;
              })()}</p>
            </div>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={cancelSave}>Cancel</button>
              <button className="btn-confirm" onClick={confirmSave}>Yes, Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Leave/refresh prompt while recording */}
      {showLeavePrompt && (
        <div className="modal-overlay" onClick={() => setShowLeavePrompt(false)}>
          <div className="modal-content-save" onClick={(e) => e.stopPropagation()}>
            <h3>Recording in progress</h3>
            <p>Do you want to stop recording before leaving this page?</p>
            <div className="modal-actions">
              <button
                className="btn-confirm"
                onClick={() => {
                  handleSave();
                  handleStopRecording();
                  setShowLeavePrompt(false);
                }}
              >
                Save & Stop
              </button>
              <button
                className="btn-cancel"
                onClick={() => {
                  handleStopRecording();
                  setShowLeavePrompt(false);
                }}
              >
                Stop without saving
              </button>
              <button className="btn-cancel" onClick={() => setShowLeavePrompt(false)}>
                Stay on page
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="recording-header">
        <div className="header-left">
          <button className="btn-back" onClick={onBack}>← Back</button>
          <h2>{test?.name || 'Untitled test'}</h2>
          {test?.executionMode && (
            <span className="execution-badge">
              {isExecuting ? '⟳ Executing' : testStatus === 'success' ? '✓ Passed' : testStatus === 'failed' ? '✗ Failed' : 'Ready'}
            </span>
          )}
        </div>
        <div className="header-right">
          {!test?.executionMode && (
            <>
              {testContent && (
                <button
                  className={`btn-header btn-ghost ${showCodePanel ? 'active' : ''}`}
                  onClick={() => {
                    setShowCodePanel(!showCodePanel);
                    if (!showCodePanel && testContent) {
                      setEditedCode(testContent);
                      setIsEditingCode(false);
                    }
                  }}
                >
                  {showCodePanel ? 'Hide Code' : 'View Code'}
                </button>
              )}
              {/* <button className="btn-header">Environment {test?.environment || 'Production'}</button>
              <button className="btn-header">Parameters</button> */}
              {savedTestId && testSteps.length > 0 && (
                <button
                  className="btn-header"
                  onClick={handleImproveNames}
                  disabled={improvingNames}
                  title="Rewrite the step descriptions in plain English. Does not change the test itself."
                >
                  {improvingNames ? 'Improving...' : 'Improve names'}
                </button>
              )}
              {improveResult && (
                <span style={{ fontSize: '0.8rem', color: '#0aa197', alignSelf: 'center', marginRight: '4px' }}>
                  {improveResult}
                </span>
              )}
              <button 
                className="btn-header btn-save" 
                onClick={handleSave}
                disabled={isSaving || testSteps.length === 0}
                title={isSaving ? 'Saving...' : testSteps.length === 0 ? 'No steps to save' : 'Save test'}
              >
                {isSaving ? 'Saving...' : isSaved ? 'Update' : 'Save'}
              </button>
              {/* <button className="btn-header">Dev tools</button>
              <button className="btn-header">Browser</button>
              <button className="btn-header">Log</button> */}
            </>
          )}
          {test?.executionMode && testOutput.length > 0 && (
            <div className="execution-output-summary">
              {testStatus === 'success' && <span className="output-success">✓ Test Passed</span>}
              {testStatus === 'failed' && <span className="output-failed">✗ Test Failed</span>}
              {testStatus === 'error' && <span className="output-error">⚠ Error</span>}
              {isExecuting && <span className="output-running">⟳ Running...</span>}
            </div>
          )}
        </div>
      </div>

      {/* Success Message */}
      {saveSuccessMessage && (
        <div className="success-message">
          {saveSuccessMessage}
        </div>
      )}

      <div className="recording-content" style={{ position: 'relative' }}>

        {/* Code Editor Panel */}
        {showCodePanel && testContent && (
          <div style={{
            position: 'absolute',
            top: '0px',
            right: '20px',
            width: '600px',
            maxHeight: '70vh',
            background: 'white',
            border: '1px solid #E5E7EB',
            borderRadius: '8px',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
            zIndex: 1001,
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{
              padding: '1rem',
              borderBottom: '1px solid #E5E7EB',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>Test Code</h3>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {!isEditingCode ? (
                  <button
                    onClick={() => {
                      setIsEditingCode(true);
                      setEditedCode(testContent);
                    }}
                    style={{
                      padding: '0.375rem 0.85rem',
                      background: 'var(--primary-gradient)',
                      color: 'white',
                      border: '1px solid transparent',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      boxShadow: '0 6px 14px rgba(10,161,151,0.25)'
                    }}
                  >
                    Edit
                  </button>
                ) : (
                  <>
                    <button
                      onClick={async () => {
                        // Save edited code
                        const testId = test?.dbId || test?.id || test?.test_case_id;
                        if (testId && editedCode) {
                          try {
                            const response = await fetch(`${API_BASE_URL}/api/test-cases/${testId}/code`, {
                              method: 'PUT',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ content: editedCode })
                            });
                            const data = await response.json();
                            if (data.success) {
                              setTestContent(editedCode);
                          if (Array.isArray(data.steps)) {
                            const refreshedSteps = data.steps.map((s, idx) => ({
                              id: s.step_number || idx + 1,
                              action: s.action,
                              selector: s.selector,
                              value: s.value,
                              code: s.code,
                              actual: s.actual,
                              expected: s.expected,
                              dbId: s.id || s.step_id,
                              duration: 0,
                              status: 'completed'
                            }));
                            setTestSteps(refreshedSteps);
                          } else {
                            // Fallback: refetch the test case to keep UI in sync
                            try {
                              const testResponse = await fetch(`${API_BASE_URL}/api/test-cases/${testId}`);
                              const testData = await testResponse.json();
                              if (testData.success && Array.isArray(testData.testCase?.steps)) {
                                const refreshedSteps = testData.testCase.steps.map((s, idx) => ({
                                  id: s.step_number || idx + 1,
                                  action: s.action,
                                  selector: s.selector,
                                  value: s.value,
                                  code: s.code,
                                  actual: s.actual,
                                  expected: s.expected,
                                  dbId: s.id,
                                  duration: 0,
                                  status: 'completed'
                                }));
                                setTestSteps(refreshedSteps);
                              }
                            } catch (fetchErr) {
                              console.error('Error refreshing steps after code update:', fetchErr);
                            }
                          }
                              setIsEditingCode(false);
                              setShowMessage({ show: true, message: 'Code updated successfully', type: 'success' });
                              setTimeout(() => setShowMessage({ show: false, message: '', type: 'success' }), 3000);
                            } else {
                              setShowMessage({ show: true, message: 'Error updating code: ' + data.error, type: 'error' });
                              setTimeout(() => setShowMessage({ show: false, message: '', type: 'success' }), 3000);
                            }
                          } catch (error) {
                            setShowMessage({ show: true, message: 'Error updating code: ' + error.message, type: 'error' });
                            setTimeout(() => setShowMessage({ show: false, message: '', type: 'success' }), 3000);
                          }
                        } else {
                          // If no testId, just update local state
                          setTestContent(editedCode);
                          setIsEditingCode(false);
                          setShowMessage({ show: true, message: 'Code updated (will be saved when you save the test)', type: 'info' });
                          setTimeout(() => setShowMessage({ show: false, message: '', type: 'success' }), 3000);
                        }
                      }}
                      style={{
                        padding: '0.375rem 0.85rem',
                        background: 'var(--primary-gradient)',
                        color: 'white',
                        border: '1px solid transparent',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        boxShadow: '0 6px 14px rgba(10,161,151,0.25)'
                      }}
                    >
                      Save
                    </button>
                    <button
                      onClick={() => {
                        setIsEditingCode(false);
                        setEditedCode(testContent);
                      }}
                      style={{
                        padding: '0.375rem 0.75rem',
                        background: '#F3F4F6',
                        color: '#374151',
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '0.875rem'
                      }}
                    >
                      Cancel
                    </button>
                  </>
                )}
                <button
                  onClick={() => setShowCodePanel(false)}
                  style={{
                    padding: '0.375rem 0.75rem',
                    background: 'transparent',
                    border: '1px solid #E5E7EB',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.875rem'
                  }}
                >
                  ×
                </button>
              </div>
            </div>
            <div style={{
              flex: 1,
              overflow: 'auto',
              padding: '1rem'
            }}>
              {isEditingCode ? (
                <textarea
                  value={editedCode}
                  onChange={(e) => setEditedCode(e.target.value)}
                  style={{
                    width: '100%',
                    minHeight: '400px',
                    fontFamily: 'Monaco, Menlo, "Ubuntu Mono", Consolas, "source-code-pro", monospace',
                    fontSize: '14px',
                    lineHeight: '1.5',
                    padding: '1rem',
                    border: '1px solid #E5E7EB',
                    borderRadius: '4px',
                    background: '#1e1e1e',
                    color: '#d4d4d4',
                    resize: 'vertical',
                    whiteSpace: 'pre',
                    overflowWrap: 'normal',
                    overflowX: 'auto'
                  }}
                  spellCheck={false}
                />
              ) : (
                <pre style={{
                  margin: 0,
                  padding: '1rem',
                  background: '#1e1e1e',
                  color: '#d4d4d4',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontFamily: 'Monaco, Menlo, "Ubuntu Mono", Consolas, "source-code-pro", monospace',
                  lineHeight: '1.5',
                  whiteSpace: 'pre',
                  overflow: 'auto',
                  maxHeight: '60vh'
                }}>
                  {testContent}
                </pre>
              )}
            </div>
          </div>
        )}

        <div className="steps-panel">
          <h3>Steps {displayedSteps.length > 0 && `(${displayedSteps.length})`}</h3>
          <div className="steps-list">
            {displayedSteps.length === 0 ? (
              <div className="no-steps">No steps recorded yet. Start recording to begin.</div>
            ) : (
              <>
                {displayedSteps.map((step, index) => {
                  const isStepExecuting = isExecuting && executingStepIndex === index;
                  const isExecuted = executingStepIndex > index;
                  const stepStatus = step.status || (isStepExecuting ? 'running' : isExecuted ? 'completed' : 'pending');
                  
                  return (
                    <div className="step-with-insert" key={step.id || index}>
                    <div className="step-insert-row between">
                      <div className="step-line" />
                      <button
                        className="add-step-btn"
                        onClick={() => insertWaitStep(index, 'above')}
                        title="Add step above"
                      >
                        + Add Step
                      </button>
                      <div className="step-line" />
                    </div>
                    <div
                      className={`step-item ${isStepExecuting ? 'executing' : ''} ${stepStatus}`}
                    >
                      <div className="step-number">{step.id || index + 1}</div>
                      <div className="step-content">
                        {/* English Description Only */}
                        <div className="step-action">
                          {step.englishDescription || step.action}
                        </div>
                        
                        {step.duration !== undefined && step.duration > 0 && (
                          <div className="step-duration">{step.duration}s</div>
                        )}
                      </div>
                      {!isStepExecuting && (
                        <div className="step-actions">
                          {/* Enhanced Selector Info Button */}
                          {(step.selectorAlternatives && step.selectorAlternatives.length > 0) && (
                            <button
                              className="step-action-btn selector-btn"
                              onClick={() => {
                                setSelectedStepForSelector(step);
                                setShowSelectorPanel(true);
                              }}
                              title="View selector alternatives"
                            >
                              🎯
                            </button>
                          )}
                          <button
                            className="step-action-btn"
                            onClick={() => {
                              // Extract selector from step if not already present
                              let selector = step.selector;
                              // Try to parse selector from code first
                              const codeLine = step.code || '';
                              let navigateUrl = step.value || step.url || '';
                              let parsedValue = step.value;
                              // An assertion's expected text must come from the assertion
                              // itself. Pre-filling it from the element name produced
                              // nonsense like toHaveText('welcome-to-zoodpay-admin-panel').
                              // An upload step is either a plain path or a file carried
                              // inside the test: { name, mimeType, buffer: Buffer.from('...') }
                              // setInputFiles when the step aims at the file input,
                              // setFiles when it answers the dialog an upload button opened.
                              const uploadEmbedded = (step.code || '').match(
                                /set(?:Input)?Files\(\s*\{\s*name:\s*(['"])([\s\S]*?)\1\s*,\s*mimeType:\s*(['"])([\s\S]*?)\3\s*,\s*buffer:\s*Buffer\.from\(\s*(['"])([\s\S]*?)\5\s*(,\s*['"]base64['"]\s*)?\)/
                              );
                              const uploadFromCode = (step.code || '').match(/set(?:Input)?Files\((['"])([\s\S]*?)\1\)/);
                              // The button a file-chooser upload clicks, so re-opening the
                              // step shows it instead of an empty field.
                              const uploadButtonFromCode = (step.code || '').match(
                                /waitForEvent\(\s*['"]filechooser['"]\s*\)[\s\S]*?await\s+page\.locator\(\s*(['"])([\s\S]*?)\1\s*\)\s*\.click\(/
                              );
                              const unescapeContent = (v) => String(v || '')
                                .replace(/\\n/g, '\n')
                                .replace(/\\r/g, '\r')
                                .replace(/\\'/g, "'")
                                .replace(/\\\\/g, '\\');
                              // The locator exactly as Playwright wrote it, e.g.
                              //   page.getByRole('heading', { name: 'X' })
                              // Re-expressing this as page.locator('role=heading[name="X"]')
                              // changes how it matches - getByRole matches a NAME SUBSTRING,
                              // the role= string matches EXACTLY - so a click that worked
                              // became an assert that found nothing. Carry it, don't translate it.
                              const locatorFromAction = (step.code || '').match(
                                /await\s+(page\.[\s\S]*?)\.(?:click|fill|press|check|uncheck|hover|selectOption|setInputFiles)\s*\(/
                              );
                              const locatorFromAssert = (step.code || '').match(
                                /expect\(\s*(page\.[\s\S]*?)\s*\)\s*\.\s*(?:not\s*\.\s*)?to[A-Za-z]/
                              );
                              const locatorExpr = (locatorFromAssert && locatorFromAssert[1])
                                || (locatorFromAction && locatorFromAction[1])
                                || '';

                              const expectedFromCode = (step.code || '').match(/to(?:HaveText|ContainText)\((['"])([\s\S]*?)\1\s*(?:,[\s\S]*?)?\)/);
                              const parsedExpected = expectedFromCode ? expectedFromCode[2] : '';
                              if (!selector && codeLine) {
                                // A selector often contains the other quote character, e.g.
                                // locator('[data-testid="panel"]'). Matching with [^'"]+ stops
                                // at that inner quote and finds nothing, so the Selector box came
                                // up EMPTY and saving then wiped the selector.
                                const testIdMatch = codeLine.match(/getByTestId\((['"])([\s\S]*?)\1\)/);
                                const roleMatch = codeLine.match(/getByRole\((['"])[\s\S]*?\1,\s*\{\s*name:\s*(['"])([\s\S]*?)\2\s*\}\)/);
                                const locatorMatch = codeLine.match(/locator\((['"])([\s\S]*?)\1\)/);
                                const textMatch = codeLine.match(/getByText\((['"])([\s\S]*?)\1\)/);
                                const gotoMatch = codeLine.match(/goto\(['"]([^'"]+)['"]\)/);
                                const fillMatch = codeLine.match(/\.fill\(\s*(?:td\(\s*)?['"]([^'"]*)['"]/);
                                const selectMatch = codeLine.match(/selectOption\(['"]([^'"]+)['"]\)/);
                                const pressMatch = codeLine.match(/press\(['"]([^'"]+)['"]\)/);
                                const waitMatch = codeLine.match(/waitForTimeout\((\d+)\)/);
                                const assertMatch = codeLine.match(/to(?:HaveText|ContainText)\((['"])([\s\S]*?)\1\s*(?:,[\s\S]*?)?\)/);
                                if (testIdMatch) selector = `[data-testid="${testIdMatch[2]}"]`;
                                else if (locatorMatch) selector = locatorMatch[2];
                                else if (roleMatch) selector = roleMatch[3];
                                else if (textMatch) selector = textMatch[2];
                                if (!navigateUrl && gotoMatch) navigateUrl = gotoMatch[1];
                                if (!parsedValue && fillMatch) parsedValue = fillMatch[1];
                                else if (!parsedValue && selectMatch) parsedValue = selectMatch[1];
                                else if (!parsedValue && pressMatch) parsedValue = pressMatch[1];
                                else if (!parsedValue && waitMatch) parsedValue = waitMatch[1];
                                else if (!parsedValue && assertMatch) parsedValue = assertMatch[2];
                              }

                              if (!selector && step.action) {
                                // Try to extract selector from action text
                                const action = step.action;
                                // Pattern: "Click #selector" or "Fill #selector with..."
                                const selectorMatch = action.match(/(?:Click|Fill|Select|Check|Uncheck|Hover)\s+(#[\w-]+|\.\w+|\[[\w-]+[^\]]*\])/);
                                if (selectorMatch) {
                                  selector = selectorMatch[1];
                                } else {
                                  // Try to find any selector-like pattern
                                  const anySelector = action.match(/([#.[\w-]+)/);
                                  if (anySelector && !anySelector[1].includes(' ')) {
                                    selector = anySelector[1];
                                  }
                                }
                              }
                              // Determine actionType from step action
                              const action = (step.action || step.code || '').toLowerCase();
                              let actionType = 'click'; // default
                              const codeStr = String(step.code || '');
                              // Work the type out from the CODE first - it is the truth.
                              // The English text cannot tell an exact assert from a
                              // contains assert, and any English containing the word
                              // "await" used to be misread as a Wait step.
                              // A button-driven upload is a custom block by storage, but
                              // it is an UPLOAD to the tester - so it must open in the
                              // upload editor, not as raw script. Checked before the
                              // custom-step test, which would otherwise claim it.
                              if (codeStr.includes('setInputFiles') || codeStr.includes('fileChooser.setFiles')) actionType = 'upload';
                              else if (codeStr.includes('// @custom-step')) actionType = 'custom';
                              else if (codeStr.includes('toBeVisible')) actionType = 'assertVisible';
                              else if (codeStr.includes('toContainText')) actionType = 'assertContains';
                              else if (codeStr.includes('toHaveText')) actionType = 'assert';
                              else if (codeStr.includes('.goto(')) actionType = 'navigate';
                              else if (codeStr.includes('.fill(')) actionType = 'fill';
                              else if (codeStr.includes('.selectOption(')) actionType = 'select';
                              else if (codeStr.includes('.uncheck(')) actionType = 'uncheck';
                              else if (codeStr.includes('.check(')) actionType = 'check';
                              else if (codeStr.includes('.hover(')) actionType = 'hover';
                              else if (codeStr.includes('.press(')) actionType = 'press';
                              else if (codeStr.includes('waitFor')) actionType = 'wait';
                              else if (codeStr.includes('.click(')) actionType = 'click';
                              else if (action.includes('is visible')) actionType = 'assertVisible';
                              else if (action.includes('appears in')) actionType = 'assertContains';
                              else if (action.includes('exactly the text')) actionType = 'assert';
                              else if (action.includes('navigate')) actionType = 'navigate';
                              else if (action.includes('fill')) actionType = 'fill';
                              else if (action.includes('assert') || action.includes('verify')) actionType = 'assert';
                              else if (action.includes('wait for')) actionType = 'wait';
                              else if (action.includes('select')) actionType = 'select';
                              else if (action.includes('check') && !action.includes('uncheck')) actionType = 'check';
                              else if (action.includes('uncheck')) actionType = 'uncheck';
                              else if (action.includes('hover')) actionType = 'hover';
                              else if (action.includes('press')) actionType = 'press';

                              const sanitizeSelector = (sel) => {
                                if (!sel) return sel;

                                // Playwright records test-id targets in its own internal
                                // form:  internal:testid=[data-testid="foo"s]
                                // The trailing "s" is Playwright's case-sensitivity flag,
                                // NOT part of CSS. Left in, page.locator() throws
                                // "is not a valid selector" at run time.
                                const testId = sel.match(/\[data-testid=(['"])([\s\S]*?)\1\s*s?\]/);
                                if (testId) return `[data-testid="${testId[2]}"]`;

                                let cleaned = sel.replace(/^internal:/, '');
                                cleaned = cleaned.replace(/^testid\s+data\s+testid\s+(.+?)\s+s$/i, '$1');
                                cleaned = cleaned.replace(/^testid[:=]\s*/i, '');
                                cleaned = cleaned.replace(/(\[data-testid="[^"]+"\])s$/i, '$1');
                                // strip Playwright's case-sensitivity flag from any
                                // remaining attribute selector: [attr="v"s] -> [attr="v"]
                                cleaned = cleaned.replace(/(\[[^\]]*")\s*s\]/g, '$1]');
                                cleaned = cleaned.replace(/([A-Za-z0-9_-]+)s$/, '$1');
                                return cleaned.trim();
                              };

                              setEditingStep({ 
                                ...step, 
                                index: step.originalIndex ?? index, 
                                selector: sanitizeSelector(selector || step.selector || ''),
                                actionType: actionType,
                                locatorExpr,
                                derivedSelector: sanitizeSelector(selector || step.selector || ''),
                                script: actionType === 'custom'
                                  ? String(step.code || '')
                                      .split('\n')
                                      .map(l => l.trim())
                                      .filter(l => l && l !== '// @custom-step' && l !== '// @end-custom-step')
                                      .join('\n')
                                  : (step.script || ''),
                                value: actionType === 'upload'
                                  ? (uploadEmbedded ? '' : (uploadFromCode ? uploadFromCode[2] : (step.value || '')))
                                  : actionType === 'navigate' ? (navigateUrl || step.value || '') : (parsedValue || step.value || ''),
                                uploadName: uploadEmbedded ? uploadEmbedded[2] : '',
                                uploadMime: uploadEmbedded ? uploadEmbedded[4] : '',
                                uploadIsBase64: !!(uploadEmbedded && uploadEmbedded[7]),
                                uploadContent: uploadEmbedded ? unescapeContent(uploadEmbedded[6]) : '',
                                uploadButton: uploadButtonFromCode ? uploadButtonFromCode[2] : '',
                                url: actionType === 'navigate' ? (navigateUrl || step.url || '') : step.url,
                                expected: step.expected || ((actionType === 'assert' || actionType === 'assertContains') ? parsedExpected : step.expected)
                              });
                              setShowStepEditor(true);
                            }}
                            title="Edit step"
                          >
                            ✎
                          </button>
                          <button
                            className="step-action-btn delete-btn"
                            onClick={() => {
                              const stepNumber = index + 1;
                              const stepAction = step.action || 'this step';
                              const performDeleteStep = async (stepToDelete, stepIndex) => {
                                if (stepToDelete.dbId) {
                                  // Delete from database
                                  try {
                                    const response = await fetch(`${API_BASE_URL}/api/steps/${stepToDelete.dbId}`, {
                                      method: 'DELETE',
                                    });
                                    const data = await response.json();
                                    if (data.success) {
                                      setTestSteps(prev => prev.filter((s, i) => i !== stepIndex));
                                      // Reload test if it's from DB - RELOAD BOTH STEPS AND CONTENT
                                      const testId = test?.dbId || test?.id || test?.test_case_id;
                                      if (testId) {
                                        const testResponse = await fetch(`${API_BASE_URL}/api/test-cases/${testId}`);
                                        const testData = await testResponse.json();
                                        if (testData.success && testData.testCase) {
                                          // Update steps
                                          if (testData.testCase.steps) {
                                            // Steps recorded in this session but not yet saved are
                                            // not in the database. Replacing the list wholesale
                                            // silently threw them away when a step was deleted.
                                            const unsaved = (testStepsRef.current || []).filter(st => !st.dbId);
                                            setTestSteps([...testData.testCase.steps.map((s, idx) => ({
                                              id: s.step_number || idx + 1,
                                              action: s.action,
                                              selector: s.selector,
                                              value: s.value,
                                              code: s.code,
                                              actual: s.actual,
                                              expected: s.expected,
                                              dbId: s.id,
                                              duration: 0,
                                              status: 'completed'
                                            })), ...unsaved]);
                                          }
                                          // CRITICAL: Update test content (code) after step deletion
                                          if (testData.testCase.content) {
                                            setTestContent(testData.testCase.content);
                                            console.log('[FRONTEND] ✓ Updated test content after step deletion');
                                          }
                                        }
                                      }
                                      setShowMessage({ show: true, message: 'Step deleted successfully', type: 'success' });
                                      setTimeout(() => setShowMessage({ show: false, message: '', type: 'success' }), 3000);
                                    } else {
                                      setShowMessage({ show: true, message: 'Error deleting step: ' + data.error, type: 'error' });
                                      setTimeout(() => setShowMessage({ show: false, message: '', type: 'success' }), 3000);
                                    }
                                  } catch (error) {
                                    console.error('Error deleting step:', error);
                                    setShowMessage({ show: true, message: 'Error deleting step', type: 'error' });
                                    setTimeout(() => setShowMessage({ show: false, message: '', type: 'success' }), 3000);
                                  }
                                } else {
                                  // This step has no database row yet - it exists
                                  // only in the recorder's memory. Tell the server
                                  // to forget it as well, or it would be written
                                  // back out on save and appear to return.
                                  if (sendMessage) {
                                    sendMessage('forgetStep', {
                                      testId: test?.dbId || test?.id || test?.test_case_id,
                                      stepIndex
                                    });
                                  }
                                  setTestSteps(prev =>
                                    prev
                                      .filter((s, i) => i !== stepIndex)
                                      .map((s, i) => ({
                                        ...s,
                                        id: i + 1
                                      }))
                                  );
                                  setShowMessage({ show: true, message: 'Step deleted successfully', type: 'success' });
                                  setTimeout(() => setShowMessage({ show: false, message: '', type: 'success' }), 3000);
                                }
                              };

                              setConfirmModal({
                                isOpen: true,
                                message: `Are you sure you want to delete step ${stepNumber} (${stepAction})? This action cannot be undone.`,
                                type: 'danger',
                                onConfirm: async () => {
                                  setConfirmModal({ isOpen: false, message: '', onConfirm: null, type: 'danger' });
                                  await performDeleteStep(step, step.originalIndex ?? index);
                                }
                              });
                            }}
                            title="Delete step"
                          >
                            🗑
                          </button>
                        </div>
                      )}
                      {stepStatus === 'completed' && (
                        <div className="step-status completed">✓</div>
                      )}
                      {stepStatus === 'running' && (
                        <div className="step-status running">⟳</div>
                      )}
                      {stepStatus === 'failed' && (
                        <div className="step-status failed">✗</div>
                      )}
                      {stepStatus === 'pending' && !isStepExecuting && (
                        <div className="step-status pending">○</div>
                      )}
                    </div>
                    <div className="step-insert-row between">
                      <div className="step-line" />
                      <button
                        className="add-step-btn"
                        onClick={() => insertWaitStep(index, 'below')}
                        title="Add step below"
                      >
                        + Add Step
                      </button>
                      <div className="step-line" />
                    </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </div>

        <div className="browser-panel">
          <div className="browser-header">
            <span className="browser-status">
              {isExecuting 
                ? `Executing... Step ${executingStepIndex + 1} of ${testSteps.length}` 
                : isRecording 
                  ? (isPaused ? 'Recording Paused - Click Resume to continue' : 'Recording with Playwright Codegen...') 
                  : test?.executionMode 
                    ? (testStatus === 'success' ? 'Test Passed ✓' : testStatus === 'failed' ? 'Test Failed ✗' : 'Ready')
                    : 'Ready'}
            </span>
            <div className="browser-controls">
              {!test?.executionMode && (
                <>
                  <button
                    className={`btn-record ${isRecording ? 'recording' : ''}`}
                    onClick={isRecording ? handleStopRecording : requestStartRecording}
                    disabled={!isConnected}
                  >
                    {isRecording ? '⏹ Stop' : '⏺ Start Recording'}
                  </button>
                  {isRecording && (
                    <>
                      <button
                        className={`btn-pause ${isPaused ? 'paused' : ''}`}
                        onClick={isPaused ? handleResumeRecording : handlePauseRecording}
                        disabled={!isConnected}
                        title={isPaused ? 'Resume recording' : 'Pause recording'}
                      >
                        {isPaused ? '▶ Resume' : '⏸ Pause'}
                      </button>
                    </>
                  )}
                </>
              )}
              {test?.executionMode && isExecuting && !testStatus && (
                <div className="execution-status">
                  <span className="status-indicator running">Running...</span>
                </div>
              )}
              {test?.executionMode && testStatus && (
                <div className="execution-status">
                  <span 
                    className={`status-indicator ${testStatus} ${(testStatus === 'failed' || testStatus === 'error') ? 'clickable-error' : ''}`}
                    onClick={() => {
                      if (testStatus === 'failed' || testStatus === 'error') {
                        // Collect all error messages from testOutput
                        const errors = testOutput.filter(output => output.type === 'error' || output.type === 'failed');
                        const errorMessages = errors.map(e => e.message).join('\n');
                        if (errorMessages || errors.length > 0) {
                          setErrorDetails({
                            status: testStatus,
                            messages: errorMessages || errors.map(e => e.message || JSON.stringify(e)).join('\n'),
                            allErrors: errors
                          });
                          setShowErrorModal(true);
                        } else {
                          // If no errors in testOutput, show a generic error message
                          setErrorDetails({
                            status: testStatus,
                            messages: 'An error occurred during test execution. Please check the console for details.',
                            allErrors: []
                          });
                          setShowErrorModal(true);
                        }
                      }
                    }}
                    style={{ cursor: (testStatus === 'failed' || testStatus === 'error') ? 'pointer' : 'default' }}
                    title={(testStatus === 'failed' || testStatus === 'error') ? 'Click to view error details' : ''}
                  >
                    {testStatus === 'success' ? '✓ Passed' : testStatus === 'failed' ? '✗ Failed' : '⚠ Error'}
                  </span>
                </div>
              )}
              {/* Show error indicator even when running if there are errors in testOutput */}
              {test?.executionMode && isExecuting && testOutput.some(output => output.type === 'error' || output.type === 'failed') && (
                <div className="execution-status">
                  <span 
                    className="status-indicator error clickable-error"
                    onClick={() => {
                      const errors = testOutput.filter(output => output.type === 'error' || output.type === 'failed');
                      const errorMessages = errors.map(e => e.message).join('\n');
                      setErrorDetails({
                        status: 'error',
                        messages: errorMessages || 'An error occurred during test execution.',
                        allErrors: errors
                      });
                      setShowErrorModal(true);
                    }}
                    style={{ cursor: 'pointer' }}
                    title="Click to view error details"
                  >
                    ⚠ Error
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="browser-view">
            {/* Real browser embedded via CDP */}
            {isRecording && cdpWsUrl ? (
              <div className="browser-viewport-container" style={{ 
                width: '100%',
                height: '100%',
                position: 'relative',
                flex: 1,
                display: 'flex',
                flexDirection: 'column'
              }}>
                <iframe
                  key={cdpWsUrl} // Force re-render when URL changes
                  src={cdpWsUrl}
                  title="Embedded Real Browser"
                  className="browser-iframe"
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    flex: 1
                  }}
                  sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-modals"
                  allow="clipboard-read; clipboard-write"
                  onLoad={() => {
                    console.log('[FRONTEND] ✓ Real browser iframe loaded successfully:', cdpWsUrl);
                  }}
                  onError={(e) => {
                    console.error('[FRONTEND] ✗ Real browser iframe load error:', e, 'URL:', cdpWsUrl);
                  }}
                />
                <div className={`recording-overlay ${isPaused ? 'paused' : ''}`}>
                  <div className="recording-indicator">
                    <span className="pulse-dot"></span>
                    <span>{isPaused ? 'Recording Paused' : 'Real Browser Embedded'}</span>
                  </div>
                </div>
              </div>
            ) : isRecording ? (
              <div className="real-browser-mode">
                <div className="browser-instructions">
                  <div className={`recording-indicator-large ${isPaused ? 'paused' : ''}`}>
                    <span className="pulse-dot-large"></span>
                    <span>{isPaused ? 'Recording Paused' : 'Playwright Codegen Active'}</span>
                  </div>
                  <h3>Real Browser Ready</h3>
                  <p>
                    A live browser is open for recording. Work in that window as usual — every action is captured and shown as steps on the left.
                  </p>
                  <div className="browser-features">
                    <div className="feature">
                      <span className="feature-text">Click elements in the browser</span>
                    </div>
                    <div className="feature">
                      <span className="feature-text">Type in form fields</span>
                    </div>
                    <div className="feature">
                      <span className="feature-text">Navigate between pages</span>
                    </div>
                    <div className="feature">
                      <span className="feature-text">Steps appear here automatically</span>
                    </div>
                  </div>
                  <div className="recording-status">
                    <strong>{isPaused ? 'Recording Paused' : 'Recording in progress...'}</strong>
                    <p>
                      {isPaused 
                        ? 'Recording is paused. Click Resume to continue capturing interactions.'
                        : 'All interactions with the browser window are being captured by Playwright\'s native codegen.'
                      }
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="browser-placeholder">
                <div className="placeholder-content">
                  <h3>Ready to Record</h3>
                  <p>Click "Start Recording" to capture your actions in a live browser.</p>
                  <div className="placeholder-features">
                    <div className="placeholder-feature">
                      <span>Real browser interaction</span>
                    </div>
                    <div className="placeholder-feature">
                      <span>Instant step updates</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
{/* 
      <div className="recording-footer">
        <div className="timeline">
          <span>00:00</span>
          <div className="timeline-bar"></div>
          <span>00:00</span>
        </div>
        <div className="playback-controls">
          <button className="playback-btn">⏮</button>
          <button className="playback-btn">⏯</button>
          <button className="playback-btn">⏭</button>
          <div className="speed-controls">
            <button className="speed-btn">0.5x</button>
            <button className="speed-btn active">1x</button>
            <button className="speed-btn">2x</button>
            <button className="speed-btn">4x</button>
            <button className="speed-btn">8x</button>
            <button className="speed-btn">16x</button>
          </div>
          <label className="skip-inactive">
            <input type="checkbox" />
            skip inactive
          </label>
        </div>
      </div> */}

      {/* Delete Step Confirmation Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        message={confirmModal.message}
        title="Confirm Delete Step"
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ isOpen: false, message: '', onConfirm: null, type: 'danger' })}
        confirmText="Delete"
        cancelText="Cancel"
        type={confirmModal.type}
      />

      {/* Message Toast - Same style as TestList */}
      {showMessage.show && (
        <div className={`message-toast ${showMessage.type}`}>
          <span style={{ flex: 1 }}>{showMessage.message}</span>
          <button 
            onClick={() => setShowMessage({ show: false, message: '', type: 'success' })}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              padding: '0.25rem 0.5rem',
              fontSize: '1.2rem',
              lineHeight: 1,
              opacity: 0.8
            }}
            onMouseEnter={(e) => e.target.style.opacity = '1'}
            onMouseLeave={(e) => e.target.style.opacity = '0.8'}
          >
            ×
          </button>
        </div>
      )}

      {/* Error Details Modal */}
      {showErrorModal && errorDetails && (
        <div className="modal-overlay" onClick={() => { setShowErrorModal(false); setErrorDetails(null); }}>
          <div className="modal-content error-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '80%', maxHeight: '80vh' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ color: '#f44336', margin: 0 }}>
                {errorDetails.status === 'failed' ? '✗ Test Execution Failed' : '⚠ Test Execution Error'}
              </h3>
              <button 
                className="btn-cancel" 
                onClick={() => { setShowErrorModal(false); setErrorDetails(null); }}
                style={{ padding: '0.5rem 1rem', margin: 0 }}
              >
                ✕ Close
              </button>
            </div>
            <div style={{ 
              backgroundColor: '#f5f5f5', 
              border: '1px solid #e0e0e0', 
              borderRadius: '4px', 
              padding: '1rem',
              maxHeight: '60vh',
              overflow: 'auto',
              fontFamily: 'monospace',
              fontSize: '0.9rem',
              lineHeight: '1.5',
              color: '#333333',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word'
            }}>
              {errorDetails.messages || 'No error details available'}
            </div>
            {errorDetails.allErrors && errorDetails.allErrors.length > 0 && (
              <div style={{ marginTop: '1rem' }}>
                <h4 style={{ color: '#333333', marginBottom: '0.5rem' }}>Error Log ({errorDetails.allErrors.length}):</h4>
                <div style={{ 
                  backgroundColor: '#fafafa', 
                  padding: '0.75rem',
                  borderRadius: '4px',
                  maxHeight: '200px',
                  overflow: 'auto'
                }}>
                  {errorDetails.allErrors.map((error, idx) => (
                    <div key={idx} style={{ 
                      marginBottom: '0.5rem', 
                      padding: '0.5rem',
                      backgroundColor: '#ffffff',
                      borderRadius: '2px',
                      fontSize: '0.85rem'
                    }}>
                      <strong style={{ color: '#c62828' }}>Error {idx + 1}:</strong>
                      <div style={{ color: '#333333', marginTop: '0.25rem' }}>
                        {error.message || JSON.stringify(error)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Enhanced Selector Panel */}
      {showSelectorPanel && selectedStepForSelector && (
        <div className="modal-overlay" onClick={() => setShowSelectorPanel(false)}>
          <div className="modal-content selector-panel-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Enhanced Selector Information</h3>
              <button 
                className="btn-close" 
                onClick={() => setShowSelectorPanel(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="selector-info-section">
                <h4>Step: {selectedStepForSelector.action}</h4>
                
                <div className="current-selector">
                  <label>Current Selector:</label>
                  <div className="selector-value">
                    <code>{selectedStepForSelector.selector}</code>
                    {selectedStepForSelector.selectorType && (
                      <span className="selector-type">({selectedStepForSelector.selectorType})</span>
                    )}
                  </div>
                  {selectedStepForSelector.selectorDescription && (
                    <div className="selector-description">
                      {selectedStepForSelector.selectorDescription}
                    </div>
                  )}
                </div>

                {selectedStepForSelector.selectorAlternatives && selectedStepForSelector.selectorAlternatives.length > 0 && (
                  <div className="selector-alternatives">
                    <label>Alternative Selectors:</label>
                    {selectedStepForSelector.selectorAlternatives.map((alt, index) => (
                      <div key={index} className="alternative-selector">
                        <div className="alt-selector-header">
                          <code>{alt.selector}</code>
                          <span className="alt-selector-type">({alt.type})</span>
                        </div>
                        {alt.description && (
                          <div className="alt-selector-description">{alt.description}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {selectedStepForSelector.elementInfo && (
                  <div className="element-info">
                    <label>Element Information:</label>
                    <div className="element-details">
                      {selectedStepForSelector.elementInfo.tagName && (
                        <div><strong>Tag:</strong> {selectedStepForSelector.elementInfo.tagName}</div>
                      )}
                      {selectedStepForSelector.elementInfo.id && (
                        <div><strong>ID:</strong> {selectedStepForSelector.elementInfo.id}</div>
                      )}
                      {selectedStepForSelector.elementInfo.className && (
                        <div><strong>Class:</strong> {selectedStepForSelector.elementInfo.className}</div>
                      )}
                      {selectedStepForSelector.elementInfo.name && (
                        <div><strong>Name:</strong> {selectedStepForSelector.elementInfo.name}</div>
                      )}
                      {selectedStepForSelector.elementInfo.dataTestId && (
                        <div><strong>Test ID:</strong> {selectedStepForSelector.elementInfo.dataTestId}</div>
                      )}
                      {selectedStepForSelector.elementInfo.text && (
                        <div><strong>Text:</strong> {selectedStepForSelector.elementInfo.text}</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RecordingView;

